import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  ExportButton,
  IconButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import { Popover, Row, Skeleton, Space, Typography } from 'antd';
import { LocalizedNumber } from 'components/LocalizedNumber';
import { ScopeTabs } from 'components/Custom/tabs';
import DearSmallIcon from 'assets/images/dear_small.png';
import ExcelSmallIcon from 'assets/images/excel_small.png';
import { ReactComponent as ClockIcon } from 'assets/images/icons/clockIcon.svg';
import { ReactComponent as ConnectIcon } from 'assets/images/icons/connectIcon.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { ReactComponent as RefreshIcon } from 'assets/images/icons/refreshIcon.svg';
import { ReactComponent as UserIcon } from 'assets/images/icons/userIcon.svg';
import { ReactComponent as WarningIcon } from 'assets/images/icons/warningIcon.svg';
import QuickbooksSmallIcon from 'assets/images/quickbooks_small.png';
import XeroSmallIcon from 'assets/images/xero_small.png';
import ZohoSmallIcon from 'assets/images/zoho_small.png';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { DashboardContainer, StrongTag } from 'components/Custom/spans';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { ScopeTable } from 'components/Custom/table';
import { formatDisplayDate, formatDisplayDateRange } from 'i18n/calendarLabels';
import {
  formatPaginationSummaryText,
  localizeDigitsForLocale,
} from 'i18n/arabicDigits';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { TruncatedText } from 'components/Custom/TruncatedText';
import { EmptyPage } from 'containers/EmptyPage';
import moment from 'moment';
import { MAIN_TUTORIAL_HIDE, PAGE_SIZE_MAIN } from 'pages/home';
import { MAIN_VIEW_MODE } from 'pages/submissions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { HashLink } from 'routing/HashLink';
import { AccountType, DateOptions } from 'store/api/enums';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import {
  AdvancedUserRightsCodes,
  dashboardStatistic,
  DataLogStatus,
  DrawerType,
} from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { User } from 'store/auth/models';
import { getAuthUserLicence } from 'store/auth/selectors';
import { getCurrentSubscription } from 'store/billing/selectors';
import { Country } from 'store/common/models';
import { Organisation } from 'store/organisation/models';
import { getOrgMappings } from 'store/organisation/selectors';
import { MappedReturn } from 'store/returns/models/returnData';
import styled from 'styled-components';
import { canEditTaxReturn } from 'utils/canEditTaxReturn';
import { isSmallOrMediumPlanLocked } from 'utils/subscriptionPlanGate';
import { CardFilterConfig } from 'utils/cardFilterConfig';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { exportReturnsToExcel } from 'utils/export-returns-to-excel';
import {
  getReturnActionLabel,
  isReturnActionPrimary,
  RETURN_ACTION_BUTTON_WIDTH,
  shouldCreateNewSubmission,
  shouldShowReturnAction,
} from 'utils/return-action-label';
import {
  applyStatusFilterWithExclusion,
  DEFAULT_EXCLUDED_STATUSES,
} from 'utils/filterHelpers';
import { getProviderLink } from 'utils/get-provider-link';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { buildPwaEntityTaxReturnsNavigateUrl } from 'utils/pwa-nav';
import { Preference, PreferenceActions } from 'utils/preferences';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { StatusGetter } from 'utils/status-getter';
import {
  SubmissionsActionCell,
  SubmissionsClientCell,
  SubmissionsDueDateCell,
  SubmissionsMenuCell,
  SubmissionsMenuContext,
  SubmissionsOrgCell,
  SubmissionsPeriodCell,
  SubmissionsPreparerCell,
  SubmissionsReviewerCell,
  SubmissionsStatusCell,
  SubmissionsTableContext,
  SubmissionsTaxReturnCell,
  submissionMenuKey,
  submissionsCellApi,
} from './SubmissionsTableContext';
import { applyFilters as applyFiltersPipeline } from '../../filter-system';
import { BoardStatusSelector } from './BoardStatusSelector';
import { BoardView } from './BoardView';
import { CalendarView } from './CalendarView';
import { FilterSelectionDrawer } from './FilterSelectionDrawer';
import { FilterViewManager } from './FilterViewManager';
import { MainGuide } from './guide';
import {
  ALL_AVAILABLE_FILTERS,
  DEFAULT_VISIBLE_FILTERS,
  FILTER_SELECTION_PREFERENCE,
} from './sharedFilterConfig';

interface MainComponentProps {
  loading: boolean;
  mappedReturns?: MappedReturn[];
  countries: Country[];
  entities?: Organisation[];
  addReturnMapping: (connectionId: string) => void;
  editReturnMapping: (connectionId: string, mappingId: string) => void;
  deleteReturnMapping: (connectionId: string, mappingId: string) => void;
  vatGenerate: (
    connectionId: string,
    mappingId: string,
    dataLogId?: string
  ) => void;
  currentUser?: User;
  emptyPageCallback: () => void;
  connectEntity: () => void;
  hasEntities?: boolean;
  gettingTaxes: boolean;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  hasAdvancedUserRights: boolean;
  initialFilter?: {
    type: string;
    value?: string;
    statusFilter?: string;
    personFilter?: string;
  };
  initialViewId?: string; // View ID to load on mount (e.g., from query parameter)
  cardFilterConfig?: CardFilterConfig; // Simple filter config from dashboard cards
  onClearFiltersNavigation?: () => void; // Optional callback when filters are cleared (e.g. navigate to clean URL)
  pinnedEntityIds?: string[]; // Entity organisationIds to show first (from entities page pin preference)
  /** PWA: hide board/calendar tabs and keep submissions on list view only. */
  listViewOnly?: boolean;
}

const { Text } = Typography;

function sortByPinnedEntities<T extends { organisationId?: string }>(
  data: T[],
  pinnedIds: string[]
): T[] {
  if (!pinnedIds?.length) return data;
  return [...data].sort((a, b) => {
    const aIdx = pinnedIds.indexOf(a.organisationId ?? '');
    const bIdx = pinnedIds.indexOf(b.organisationId ?? '');
    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
    if (aIdx >= 0) return -1;
    if (bIdx >= 0) return 1;
    return 0;
  });
}

// Provider icon mapping
const providerIcons: Record<string, string> = {
  xero: XeroSmallIcon,
  dear: DearSmallIcon,
  zoho: ZohoSmallIcon,
  quickbooks: QuickbooksSmallIcon,
  excel: ExcelSmallIcon,
};

/** Full name is added to the tooltip only after a hover shows the label is cut off. */
const SubmissionEntityName = ({
  content,
  to,
  hint,
  onNavigate,
}: {
  content: string;
  to: string;
  hint: string;
  onNavigate: () => void;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const hoverTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const clearHoverTimer = () => {
    if (hoverTimer.current != null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const reveal = () => {
    const element = textRef.current;
    if (!element) return;
    setTruncated(element.scrollWidth > element.clientWidth);
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => setOpen(true), 100);
  };

  const hide = () => {
    clearHoverTimer();
    setOpen(false);
  };

  return (
    <StyledTooltip
      open={open}
      placement="top"
      title={
        truncated ? (
          <>
            {content}
            <br />
            {hint}
          </>
        ) : (
          hint
        )
      }
    >
      <StrongTag
        className="scope-entity-name-tag"
        onPointerEnter={reveal}
        onPointerLeave={hide}
        onFocus={reveal}
        onBlur={hide}
      >
        <Link className="scope-entity-name-link" to={to} onClick={onNavigate}>
          <span ref={textRef} className="scope-truncate" style={{ fontSize: 13 }}>
            {content}
          </span>
        </Link>
      </StrongTag>
    </StyledTooltip>
  );
};

const ProviderIconWrap = styled.span`
  display: inline-flex;
  width: 25px;
  min-width: 25px;
  max-width: 25px;
  justify-content: center;
  align-items: flex-start;
`;

const ViewTabsContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1598px;
  min-width: 0;
  overflow-x: hidden;

  .ant-tabs {
    margin-bottom: 0;
    width: 100%;
  }

  .ant-tabs-nav {
    margin-bottom: 0;
    width: 100%;
  }

  .ant-tabs-ink-bar,
  .ant-tabs-ink-bar-animated {
    background: var(--color-primary, #41aaaf) !important;
    background-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-tabs-top > .ant-tabs-nav::before,
  .ant-tabs-nav::before {
    border-bottom: 1px solid #f0f0f0 !important;
  }

  .ant-tabs-content-holder {
    margin-top: 35px;
    width: 100%;
    overflow-x: hidden;
  }

  .ant-tabs-content {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: visible;
  }

  .ant-tabs-tabpane {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: visible;
  }
`;

const BOARD_STATUS_PREFERENCES = 'board-status-preferences';

const ALL_STATUSES = [
  DataLogStatus.NOT_STARTED,
  DataLogStatus.SAVED,
  DataLogStatus.AWAITING_APPROVAL,
  DataLogStatus.INTERNAL_APPROVED,
  DataLogStatus.APPROVED,
  DataLogStatus.AWAITING_CLIENT_APPROVAL,
  DataLogStatus.CLIENT_APPROVED,
  DataLogStatus.LOCKED,
  DataLogStatus.SUBMITTED,
];

export const MainComponent: React.FC<MainComponentProps> = ({
  loading,
  mappedReturns,
  countries,
  entities,
  editReturnMapping,
  deleteReturnMapping,
  vatGenerate,
  emptyPageCallback,
  connectEntity,
  currentUser,
  hasEntities,
  gettingTaxes,
  handlePreferences,
  hasAdvancedUserRights,
  initialFilter,
  initialViewId,
  cardFilterConfig,
  onClearFiltersNavigation,
  pinnedEntityIds = [],
  listViewOnly = false,
}) => {
  const dispatch = useDispatch();
  const mappings = useSelector(getOrgMappings);
  const currentSubscription = useSelector(getCurrentSubscription);
  const userLicence = useSelector(getAuthUserLicence);
  const isClientLocked = isSmallOrMediumPlanLocked(
    currentSubscription,
    userLicence
  );

  const pageSizeSaved = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_MAIN,
  });
  const guidePanelHide = handlePreferences(PreferenceActions.GET, {
    name: MAIN_TUTORIAL_HIDE,
  });
  const BUTTON_TEXT = 'BUTTON_TEXT';
  const LOADING = 'LOADING';
  const DISABLE = 'DISABLE';
  const { t, i18n } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const { localizeCountryLabel, localizeReturnLabel } = useCountryLabel();

  const handleExportReturnsList = useCallback(() => {
    if (!mappedReturns?.length) return;
    void exportReturnsToExcel(mappedReturns, entities, mappings, countries, t);
  }, [mappedReturns, entities, mappings, countries, t]);

  const renderExportReturnsButton = () =>
    mappedReturns && mappedReturns.length > 0 ? (
      <ExportButton
        icon={<IconExport style={{ width: 20, height: 20 }} />}
        onClick={handleExportReturnsList}
      >
        {t('returns.export-list')}
      </ExportButton>
    ) : null;

  // cardFilterConfig (homepage card navigation) pre-filters data before the pipeline.
  // For direct navigation (no card), cardOverrideSource stays null and the pipeline uses mappedReturns.
  const [cardOverrideSource, setCardOverrideSource] = useState<any[] | null>(
    null
  );

  const [selectedContanier, setSelectedContanier] = useState('');
  const [updateDueDateFilter, setUpdateDueDateFilter] = useState(false);
  const [updateStatusFilter, setUpdateStatusFilter] = useState(false);
  const [initialStatusFilter, setInitialStatusFilter] = useState<string[]>([]);
  const [showCard, setShowCard] = React.useState(!guidePanelHide && !pwaMobileNav);
  const [selectedPresetFilters, setSelectedPresetFilters] = useState<
    Array<Record<string, string>>
  >([]);
  // Single shared filter state for list, board, and calendar so filters persist when switching tabs
  const [sharedFiltersState, setSharedFiltersState] = useState<any>({});
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  // Single source of truth for all three views.
  // The pipeline runs synchronously in the memo — no effects, no intermediate state,
  // no separate board/calendar copy. All three views always show the same filtered data.
  const filteredReturns = useMemo(() => {
    const base = cardOverrideSource ?? mappedReturns ?? [];
    if (!sharedFiltersState || Object.keys(sharedFiltersState).length === 0) {
      return base;
    }
    return (
      applyFiltersPipeline(base, sharedFiltersState, {
        entities,
        excludeSubmittedCancelledForReturnType: true,
      }) ?? base
    );
  }, [cardOverrideSource, mappedReturns, sharedFiltersState, entities]);

  // Sorted version passed to all three views.
  const displayReturns = useMemo(
    () => sortByPinnedEntities(filteredReturns || [], pinnedEntityIds),
    [filteredReturns, pinnedEntityIds]
  );

  // Keep dataSource alias for legacy code paths (FilterViewManager callbacks, loading refs, etc.)
  const dataSource = filteredReturns;
  const setDataSource = (data: any[] | null | undefined) => {
    if (!data || data === mappedReturns) {
      setCardOverrideSource(null);
    } else {
      setCardOverrideSource(data ?? null);
    }
  };
  const displayBoardCalendarSource = displayReturns;
  const [pwaListPage, setPwaListPage] = useState(1);
  const pwaSubmissionsListRef = React.useRef<HTMLDivElement | null>(null);

  const scrollPwaSubmissionsListToTop = React.useCallback(() => {
    const listEl = pwaSubmissionsListRef.current;
    if (!listEl) return;

    // Wait a frame so the new page content is painted, then jump to list top.
    requestAnimationFrame(() => {
      listEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const goToPwaListPage = React.useCallback(
    (nextPage: number) => {
      setPwaListPage(nextPage);
      scrollPwaSubmissionsListToTop();
    },
    [scrollPwaSubmissionsListToTop]
  );

  React.useEffect(() => {
    if (!listViewOnly) {
      return;
    }
    setPwaListPage(1);
  }, [listViewOnly, displayReturns, sharedFiltersState]);

  const [statusFilterFromView, setStatusFilterFromView] = useState<
    string[] | null
  >(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null); // Preserve selected view across view mode switches
  const [clearFiltersKey, setClearFiltersKey] = useState<number>(0); // Key to force re-render when clearing filters
  const clearViewsRef = React.useRef<(() => void) | null>(null); // Ref to store clearViews function from FilterViewManager
  //const [isLoadingView, setIsLoadingView] = useState(false);
  const isLoadingViewRef = React.useRef(false);
  const loadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const cardFilterConfigAppliedRef = React.useRef<string | null>(null); // Track when cardFilterConfig has been applied
  const initialFilterAppliedRef = React.useRef<string | null>(null); // Apply legacy URL filter only once per query payload
  const pendingFiltersRef = React.useRef<any>(null); // Track filters we're waiting to apply
  const previousDataSourceRef = React.useRef<any[]>([]); // Track previous data source to detect changes
  const filtersAppliedRef = React.useRef(false); // Track if filters have been applied and we're waiting for data
  const cachedViewDataRef = React.useRef<Map<string, any[]>>(new Map()); // Cache filtered data for each view
  const preloadedFiltersRef = React.useRef<
    Map<string, { filters: any; data: any[] }>
  >(new Map()); // Preload filters and data for each view mode
  const savedViewMode = handlePreferences(PreferenceActions.GET, {
    name: MAIN_VIEW_MODE,
  });
  const [viewMode, setViewMode] = useState<'list' | 'board' | 'calendar'>(() => {
    if (listViewOnly) {
      return 'list';
    }
    return (savedViewMode as 'list' | 'board' | 'calendar') || 'list';
  });

  React.useEffect(() => {
    if (listViewOnly && viewMode !== 'list') {
      setViewMode('list');
    }
  }, [listViewOnly, viewMode]);

  // Filter selection state - using shared configuration
  const savedVisibleFilters = handlePreferences(PreferenceActions.GET, {
    name: FILTER_SELECTION_PREFERENCE,
  });
  const [visibleFilters, setVisibleFilters] = useState<string[]>(() => {
    if (
      savedVisibleFilters &&
      Array.isArray(savedVisibleFilters) &&
      savedVisibleFilters.length > 0
    ) {
      // Clean up: replace old 'provider' key with 'providerName' if present
      const cleanedFilters = savedVisibleFilters
        .filter((f) => f !== 'provider') // Remove old 'provider' key
        .map((f) => (f === 'provider' ? 'providerName' : f)); // Replace if needed (though filter above should catch it)
      // Ensure no duplicates
      return Array.from(new Set(cleanedFilters));
    }
    return DEFAULT_VISIBLE_FILTERS;
  });

  const handleOpenFilterSelection = () => {
    const drawerData: DrawerData = {
      title: t('submissions.select-filters-drawer-title'),
      subtitle: t('submissions.select-filters-subtitle'),
      type: DrawerType.FILTER_SELECTION,
      component: (
        <FilterSelectionDrawer
          availableFilters={ALL_AVAILABLE_FILTERS}
          selectedFilters={visibleFilters}
          onUpdate={(selectedFilters) => {
            setVisibleFilters(selectedFilters);
            if (currentUser) {
              handlePreferences(PreferenceActions.SET, {
                name: FILTER_SELECTION_PREFERENCE,
                value: selectedFilters,
              });
            }
          }}
          onClose={() => dispatch(turnDrawerOff())}
          handlePreferences={handlePreferences}
          currentUser={currentUser}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  // Get saved status preferences
  const savedStatusPreferences = useMemo(() => {
    if (currentUser) {
      const saved = handlePreferences(PreferenceActions.GET, {
        name: BOARD_STATUS_PREFERENCES,
      });
      if (saved && typeof saved === 'object' && saved !== null) {
        // New format with visibleStatuses and statusOrder
        if (
          'visibleStatuses' in saved &&
          Array.isArray(saved.visibleStatuses)
        ) {
          return saved;
        }
        // Old format - just an array of visible statuses
        if (Array.isArray(saved)) {
          // For old format, ensure NOT_STARTED is first in order
          const defaultStatusOrder = [
            DataLogStatus.NOT_STARTED,
            ...ALL_STATUSES.filter(
              (status) => status !== DataLogStatus.NOT_STARTED
            ),
          ];
          return { visibleStatuses: saved, statusOrder: defaultStatusOrder };
        }
      }
    }
    // Default: show all statuses except EXCEPTIONS_MODIFIED, CANCELLED, and SUBMITTED
    // Default order: NOT_STARTED first, then all other statuses
    const defaultVisibleStatuses = ALL_STATUSES.filter(
      (status) => status !== DataLogStatus.SUBMITTED
    );
    const defaultStatusOrder = [
      DataLogStatus.NOT_STARTED,
      ...ALL_STATUSES.filter((status) => status !== DataLogStatus.NOT_STARTED),
    ];
    return {
      visibleStatuses: defaultVisibleStatuses,
      statusOrder: defaultStatusOrder,
    };
  }, [handlePreferences, currentUser]);

  // Define default values for initial state
  const defaultVisibleStatuses = ALL_STATUSES.filter(
    (status) => status !== DataLogStatus.SUBMITTED
  );
  const defaultStatusOrder = [
    DataLogStatus.NOT_STARTED,
    ...ALL_STATUSES.filter((status) => status !== DataLogStatus.NOT_STARTED),
  ];

  const [visibleStatuses, setVisibleStatuses] = useState<string[]>(
    savedStatusPreferences?.visibleStatuses ||
      savedStatusPreferences ||
      defaultVisibleStatuses
  );
  const [statusOrder, setStatusOrder] = useState<string[]>(
    savedStatusPreferences?.statusOrder || defaultStatusOrder
  );

  // Update visible statuses and order when saved preferences change
  useEffect(() => {
    if (savedStatusPreferences) {
      if (
        typeof savedStatusPreferences === 'object' &&
        'visibleStatuses' in savedStatusPreferences
      ) {
        const defaultStatusOrder = [
          DataLogStatus.NOT_STARTED,
          ...ALL_STATUSES.filter(
            (status) => status !== DataLogStatus.NOT_STARTED
          ),
        ];
        setVisibleStatuses(savedStatusPreferences.visibleStatuses);
        setStatusOrder(
          savedStatusPreferences.statusOrder || defaultStatusOrder
        );
      } else if (Array.isArray(savedStatusPreferences)) {
        const defaultStatusOrder = [
          DataLogStatus.NOT_STARTED,
          ...ALL_STATUSES.filter(
            (status) => status !== DataLogStatus.NOT_STARTED
          ),
        ];
        setVisibleStatuses(savedStatusPreferences);
        setStatusOrder(defaultStatusOrder);
      }
    }
  }, [savedStatusPreferences]);

  const handleStatusSettingsSave = (config: {
    visibleStatuses: string[];
    statusOrder: string[];
  }) => {
    setVisibleStatuses(config.visibleStatuses);
    setStatusOrder(config.statusOrder);
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: BOARD_STATUS_PREFERENCES,
        value: {
          visibleStatuses: config.visibleStatuses,
          statusOrder: config.statusOrder,
        },
      });
    }
  };

  const openStatusSettings = () => {
    const drawerData: DrawerData = {
      title: t('submissions.board-status-settings-title'),
      subtitle: t('submissions.board-status-settings-subtitle'),
      type: DrawerType.BOARD_STATUS_SETTINGS,
      component: (
        <BoardStatusSelector
          visibleStatuses={visibleStatuses}
          statusOrder={statusOrder}
          onSave={handleStatusSettingsSave}
          onClose={() => dispatch(turnDrawerOff())}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  // NOTE: dataSource is now a computed value (filteredReturns memo) — no sync effect needed.

  // Watch for filtered data changes to clear loading state after data has actually updated
  useEffect(() => {
    const currentDataSource = filteredReturns || [];
    const previousDataSource = previousDataSourceRef.current || [];

    // Always cache data when filters are applied (for future instant loading)
    // Cache regardless of loading state, so we capture data even if timeout cleared loading
    const pending = pendingFiltersRef.current;
    const viewId =
      pending && typeof pending === 'object'
        ? (pending as any)?._viewId
        : undefined;
    if (viewId && filtersAppliedRef.current && currentDataSource.length >= 0) {
      cachedViewDataRef.current.set(viewId, currentDataSource);
    }

    if (isLoadingViewRef.current && filtersAppliedRef.current) {
      // Compare data source by creating a stable identifier (using IDs or indices)
      const currentIds = currentDataSource
        .map((d: any, i: number) => {
          // Try to get a unique identifier
          return d?.id || d?.mappingId || d?.organisationId || `index-${i}`;
        })
        .join(',');
      const previousIds = previousDataSource
        .map((d: any, i: number) => {
          return d?.id || d?.mappingId || d?.organisationId || `index-${i}`;
        })
        .join(',');

      const dataChanged =
        currentDataSource.length !== previousDataSource.length ||
        currentIds !== previousIds;

      if (dataChanged && currentDataSource.length >= 0) {
        // Data has changed, clear loading after a brief delay to ensure render completes
        const timeoutId = setTimeout(() => {
          if (isLoadingViewRef.current) {
            //setIsLoadingView(false);
            isLoadingViewRef.current = false;
            // Cache the filtered data for this view if we have a viewId
            const pending = pendingFiltersRef.current;
            const viewId =
              pending && typeof pending === 'object'
                ? (pending as any)?._viewId
                : undefined;
            if (viewId && currentDataSource.length > 0) {
              cachedViewDataRef.current.set(viewId, currentDataSource);
            }
            pendingFiltersRef.current = null;
            filtersAppliedRef.current = false;
            previousDataSourceRef.current = currentDataSource;
          }
        }, 200);

        return () => clearTimeout(timeoutId);
      } else {
        // Data hasn't changed - this means filters were applied but resulted in the same data
        // This is valid (e.g., same filters applied again, or filters that don't change the visible data)
        // Clear loading after a brief delay to allow the status filter to be applied
        if (previousDataSource.length > 0) {
          // We have previous data and data hasn't changed, clear loading after a delay
          // Reduced delay for faster UX
          const timeoutId = setTimeout(() => {
            if (isLoadingViewRef.current && filtersAppliedRef.current) {
              //setIsLoadingView(false);
              isLoadingViewRef.current = false;
              pendingFiltersRef.current = null;
              filtersAppliedRef.current = false;
              previousDataSourceRef.current = currentDataSource;
            }
          }, 300);

          return () => clearTimeout(timeoutId);
        } else {
          // First time, just store the current data source
          previousDataSourceRef.current = currentDataSource;
        }
      }
    } else if (!isLoadingViewRef.current && previousDataSource.length === 0) {
      // Update previous data source when not loading (initial state)
      previousDataSourceRef.current = currentDataSource;
    }
  }, [filteredReturns, viewMode]);

  // NOTE: board/calendar filtering is now handled by the filteredReturns memo above.

  // Preserve filters and selected view when switching between List, Board, and Calendar views
  const prevViewModeRef = React.useRef<'list' | 'board' | 'calendar' | null>(
    null
  );
  // Track last applied filters and view ID for each view mode to prevent flicker and filter leakage
  const lastAppliedFiltersRef = React.useRef<{
    list?: { filters: any; viewId?: string };
    board?: { filters: any; viewId?: string };
    calendar?: { filters: any; viewId?: string };
  }>({});
  // Track if we're currently switching view modes (to prevent double application)
  const isSwitchingViewModeRef = React.useRef<boolean>(false);
  const sharedFiltersStateRef = React.useRef<any>({});
  const statusFilterFromViewRef = React.useRef<string[] | null>(null);

  // Keep ref in sync with shared filter state
  React.useEffect(() => {
    sharedFiltersStateRef.current = sharedFiltersState;
  }, [sharedFiltersState]);
  React.useEffect(() => {
    statusFilterFromViewRef.current = statusFilterFromView;
  }, [statusFilterFromView]);

  // Centralized function to clear all filters and views across all view modes
  const clearAllFiltersAndViews = React.useCallback(() => {
    // Increment clear key to force remount of all ScopeTable instances
    // This ensures all internal state (including refs) is reset
    setClearFiltersKey((prev) => prev + 1);

    // Clear shared filter state (used by list, board, and calendar)
    setSharedFiltersState({});

    // Clear status filter from view and initial status filter
    setStatusFilterFromView(null);
    setInitialStatusFilter([]);
    setUpdateStatusFilter(false);

    // Clear selected view (but don't delete saved views)
    setSelectedViewId(null);

    // Call the clearViews function from FilterViewManager to update preferences
    if (clearViewsRef.current) {
      clearViewsRef.current();
    }

    // Save to preferences that view has been reset and filters have been reset
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: 'submissions-last-accessed-view',
        value: '', // Empty string indicates no view is selected
      });
    }

    // Reset data source to original data
    if (mappedReturns) {
      setDataSource(mappedReturns);
    }

    onClearFiltersNavigation?.();
  }, [currentUser, handlePreferences, mappedReturns, onClearFiltersNavigation]);

  // Note: Filter preservation when switching views is now handled by FilterViewManager
  // which re-applies the selected view when viewMode changes. This ensures that filters
  // are properly preserved when switching between list, board, and calendar views.

  // Function to preload filters for all view modes when a view is selected
  const preloadViewForAllModes = React.useCallback(
    (viewId: string, filters: any, filteredData?: any[]) => {
      if (!viewId || !filters || !mappedReturns) return;

      // Clear old preloaded data for this view (in case view filters changed)
      // Remove all entries for this viewId across all modes
      const keysToDelete: string[] = [];
      preloadedFiltersRef.current.forEach((_, key) => {
        if (key.endsWith(`-${viewId}`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => preloadedFiltersRef.current.delete(key));

      // Store preloaded filters for all three view modes
      // Don't apply filters here - they will be applied when switching view modes
      ['list', 'board', 'calendar'].forEach((mode) => {
        const preloadKey = `${mode}-${viewId}`;
        preloadedFiltersRef.current.set(preloadKey, {
          filters,
          data: filteredData || [],
        });
      });
    },
    [mappedReturns]
  );

  useEffect(() => {
    // Track viewMode changes for FilterViewManager
    if (prevViewModeRef.current === null) {
      prevViewModeRef.current = viewMode;
      return; // Don't apply filters on initial mount
    }

    // Only apply filters when switching view modes (not on initial mount)
    if (
      selectedViewId &&
      prevViewModeRef.current !== viewMode &&
      prevViewModeRef.current !== null
    ) {
      // Prevent double application during view mode switch
      if (isSwitchingViewModeRef.current) {
        prevViewModeRef.current = viewMode;
        return;
      }

      isSwitchingViewModeRef.current = true;

      const preloadKey = `${viewMode}-${selectedViewId}`;
      const preloaded = preloadedFiltersRef.current.get(preloadKey);

      // With shared filter state, filters already persist across tabs - just track for view
      if (preloaded && preloaded.filters) {
        lastAppliedFiltersRef.current[viewMode] = {
          filters: preloaded.filters,
          viewId: selectedViewId,
        };
      } else {
        lastAppliedFiltersRef.current[viewMode] = {
          filters: sharedFiltersState,
          viewId: selectedViewId,
        };
      }
      isSwitchingViewModeRef.current = false;
    } else {
      isSwitchingViewModeRef.current = false;
    }

    // Update prevViewModeRef after processing
    prevViewModeRef.current = viewMode;
  }, [viewMode, selectedViewId, sharedFiltersState]);

  // Apply card filter config (new simple system) - takes priority over initialFilter
  useEffect(() => {
    let isMounted = true;

    // Wait for mappedReturns; currentUser only required when config has user filter (my view)
    const hasUserFilter = cardFilterConfig?.filters?.user;
    const canRun =
      cardFilterConfig &&
      mappedReturns &&
      Array.isArray(mappedReturns) &&
      (!hasUserFilter || currentUser);
    if (canRun) {
      // returnName filter can match by mappingName without entities; entity lookup is optional for return type name
      // If mappedReturns is empty, still apply filters (they'll just result in empty array)
      // This ensures filters are set in UI even if data hasn't loaded yet
      let filteredData = mappedReturns;

      // Type of Returns config is built in getTypeOfReturnsFilterConfig: manager view has no user filter,
      // my view has user filter. Apply config as-is (do not strip user filter).

      // Track nextDueDate UI value when it's a due-date type (overdue, dueToday, etc.) for filter display
      let nextDueDateUiValue: [string, string] | string | null = null;

      // Apply each filter in the config
      Object.keys(cardFilterConfig.filters).forEach((filterKey) => {
        const filter = cardFilterConfig.filters[filterKey];
        const filterValue = filter.value;

        if (filterKey === 'user' && Array.isArray(filterValue)) {
          filteredData = filteredData.filter((item) => {
            return filterValue.some((userName: string) => {
              if (!userName || typeof userName !== 'string') return false;
              const userNameLower = userName.toLowerCase().trim();

              // Unassigned: include rows with no dataLog or no assigned user in any role
              if (userNameLower === 'unassigned') {
                if (!item.dataLog) return true;
                const hasPreparer = !!(
                  item.dataLog?.preparerUser &&
                  (item.dataLog.preparerUser.firstName ||
                    item.dataLog.preparerUser.email)
                );
                const hasReviewer = !!(
                  item.dataLog?.reviewerUser &&
                  (item.dataLog.reviewerUser.firstName ||
                    item.dataLog.reviewerUser.email)
                );
                const hasClient = !!(
                  item.dataLog?.clientUser &&
                  (item.dataLog.clientUser.firstName ||
                    item.dataLog.clientUser.email)
                );
                return !hasPreparer && !hasReviewer && !hasClient;
              }

              if (!item.dataLog) return false;

              // Check preparer
              if (item.dataLog?.preparerUser) {
                const preparerName =
                  `${item.dataLog.preparerUser.firstName || ''} ${
                    item.dataLog.preparerUser.lastName || ''
                  }`.trim() ||
                  item.dataLog.preparerUser.email ||
                  '';
                if (
                  preparerName.toLowerCase() === userNameLower ||
                  preparerName.toLowerCase().includes(userNameLower)
                ) {
                  return true;
                }
              }

              // Check reviewer
              if (item.dataLog?.reviewerUser) {
                const reviewerName =
                  `${item.dataLog.reviewerUser.firstName || ''} ${
                    item.dataLog.reviewerUser.lastName || ''
                  }`.trim() ||
                  item.dataLog.reviewerUser.email ||
                  '';
                if (
                  reviewerName.toLowerCase() === userNameLower ||
                  reviewerName.toLowerCase().includes(userNameLower)
                ) {
                  return true;
                }
              }

              // Check client
              if (item.dataLog?.clientUser) {
                const clientName =
                  `${item.dataLog.clientUser.firstName || ''} ${
                    item.dataLog.clientUser.lastName || ''
                  }`.trim() ||
                  item.dataLog.clientUser.email ||
                  '';
                if (
                  clientName.toLowerCase() === userNameLower ||
                  clientName.toLowerCase().includes(userNameLower)
                ) {
                  return true;
                }
              }

              return false;
            });
          });
        } else if (filterKey === 'status' && Array.isArray(filterValue)) {
          // Status filter
          filteredData = filteredData.filter((item) => {
            return filterValue.includes(item.status);
          });
        } else if (filterKey === 'nextDueDate') {
          // Due date filter - handle DateOptions, due-date types (overdue, dueToday, etc.), and date ranges
          const now = new Date();
          const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );
          const weekFromNow = new Date(today);
          weekFromNow.setDate(weekFromNow.getDate() + 7);
          const monthFromNow = new Date(today);
          monthFromNow.setMonth(monthFromNow.getMonth() + 1);
          const nextMonthFromNow = new Date(today);
          nextMonthFromNow.setMonth(nextMonthFromNow.getMonth() + 2);

          if (
            typeof filterValue === 'string' &&
            filterValue === DateOptions.THIS_MONTH
          ) {
            const currentMonthStart = new Date(
              now.getFullYear(),
              now.getMonth(),
              1
            );
            const currentMonthEnd = new Date(
              now.getFullYear(),
              now.getMonth() + 1,
              0,
              23,
              59,
              59,
              999
            );
            filteredData = filteredData.filter((row) => {
              if (!row.nextDueDate) return false;
              const dueDate = new Date(row.nextDueDate);
              return dueDate >= currentMonthStart && dueDate <= currentMonthEnd;
            });
          } else if (
            typeof filterValue === 'string' &&
            [
              'overdue',
              'dueToday',
              'dueThisWeek',
              'dueThisMonth',
              'dueNextMonth',
              'dueLater',
            ].includes(filterValue)
          ) {
            // Returns-by-due-date card types (overdue, dueToday, etc.)
            switch (filterValue) {
              case 'overdue':
                filteredData = filteredData.filter((row) => {
                  if (!row.nextDueDate) return false;
                  const dueDate = new Date(row.nextDueDate);
                  const dueDateOnly = new Date(
                    dueDate.getFullYear(),
                    dueDate.getMonth(),
                    dueDate.getDate()
                  );
                  return dueDateOnly < today;
                });
                break;
              case 'dueToday':
                filteredData = filteredData.filter((row) => {
                  if (!row.nextDueDate) return false;
                  const dueDate = new Date(row.nextDueDate);
                  return (
                    dueDate.getDate() === today.getDate() &&
                    dueDate.getMonth() === today.getMonth() &&
                    dueDate.getFullYear() === today.getFullYear()
                  );
                });
                break;
              case 'dueThisWeek':
                filteredData = filteredData.filter((row) => {
                  if (!row.nextDueDate) return false;
                  const dueDate = new Date(row.nextDueDate);
                  return dueDate > today && dueDate <= weekFromNow;
                });
                break;
              case 'dueThisMonth':
                filteredData = filteredData.filter((row) => {
                  if (!row.nextDueDate) return false;
                  const dueDate = new Date(row.nextDueDate);
                  return dueDate > weekFromNow && dueDate <= monthFromNow;
                });
                break;
              case 'dueNextMonth':
                filteredData = filteredData.filter((row) => {
                  if (!row.nextDueDate) return false;
                  const dueDate = new Date(row.nextDueDate);
                  return dueDate > monthFromNow && dueDate <= nextMonthFromNow;
                });
                break;
              case 'dueLater':
                filteredData = filteredData.filter((row) => {
                  if (!row.nextDueDate) return false;
                  const dueDate = new Date(row.nextDueDate);
                  return dueDate > nextMonthFromNow;
                });
                break;
              default:
                break;
            }
            // Store computed date range / DateOption for UI (set in uiFiltersState below)
            let dateRangeForUi: [string, string] | string | null = null;
            switch (filterValue) {
              case 'overdue': {
                const overdueStart = new Date(2000, 0, 1);
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                dateRangeForUi = [
                  `${String(overdueStart.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}/${String(overdueStart.getDate()).padStart(
                    2,
                    '0'
                  )}/${overdueStart.getFullYear()}`,
                  `${String(yesterday.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}/${String(yesterday.getDate()).padStart(
                    2,
                    '0'
                  )}/${yesterday.getFullYear()}`,
                ];
                break;
              }
              case 'dueToday': {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                dateRangeForUi = [
                  `${String(today.getMonth() + 1).padStart(2, '0')}/${String(
                    today.getDate()
                  ).padStart(2, '0')}/${today.getFullYear()}`,
                  `${String(tomorrow.getMonth() + 1).padStart(2, '0')}/${String(
                    tomorrow.getDate()
                  ).padStart(2, '0')}/${tomorrow.getFullYear()}`,
                ];
                break;
              }
              case 'dueThisWeek':
                dateRangeForUi = [
                  `${String(today.getMonth() + 1).padStart(2, '0')}/${String(
                    today.getDate()
                  ).padStart(2, '0')}/${today.getFullYear()}`,
                  `${String(weekFromNow.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}/${String(weekFromNow.getDate()).padStart(
                    2,
                    '0'
                  )}/${weekFromNow.getFullYear()}`,
                ];
                break;
              case 'dueThisMonth':
                dateRangeForUi = DateOptions.THIS_MONTH;
                break;
              case 'dueNextMonth':
                dateRangeForUi = DateOptions.NEXT_MONTH;
                break;
              case 'dueLater': {
                const farFuture = new Date(2100, 0, 1);
                dateRangeForUi = [
                  `${String(nextMonthFromNow.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}/${String(nextMonthFromNow.getDate()).padStart(
                    2,
                    '0'
                  )}/${nextMonthFromNow.getFullYear()}`,
                  `${String(farFuture.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}/${String(farFuture.getDate()).padStart(
                    2,
                    '0'
                  )}/${farFuture.getFullYear()}`,
                ];
                break;
              }
              default:
                break;
            }
            if (dateRangeForUi !== null) {
              nextDueDateUiValue = dateRangeForUi;
            }
          } else if (Array.isArray(filterValue) && filterValue.length === 2) {
            // Date range array
            const [startDate, endDate] = filterValue;
            filteredData = filteredData.filter((row) => {
              if (!row.nextDueDate) return false;
              const dueDate = new Date(row.nextDueDate);
              const start = startDate ? new Date(startDate) : null;
              const end = endDate ? new Date(endDate) : null;
              if (start && end) {
                return dueDate >= start && dueDate <= end;
              } else if (start) {
                return dueDate >= start;
              } else if (end) {
                return dueDate <= end;
              }
              return true;
            });
          }
        } else if (
          filterKey === 'dataLog.submissionDate' ||
          filterKey === 'submissionDate'
        ) {
          // Submission date filter - handle DateOptions (like "This month") and date ranges
          if (
            typeof filterValue === 'string' &&
            filterValue === DateOptions.THIS_MONTH
          ) {
            const now = new Date();
            const currentMonthStart = new Date(
              now.getFullYear(),
              now.getMonth(),
              1
            );
            const currentMonthEnd = new Date(
              now.getFullYear(),
              now.getMonth() + 1,
              0,
              23,
              59,
              59,
              999
            );
            filteredData = filteredData.filter((row) => {
              if (!row.dataLog?.submissionDate) return false;
              const submissionDate = new Date(row.dataLog.submissionDate);
              return (
                submissionDate >= currentMonthStart &&
                submissionDate <= currentMonthEnd
              );
            });
          } else if (Array.isArray(filterValue) && filterValue.length === 2) {
            // Date range array
            const [startDate, endDate] = filterValue;
            filteredData = filteredData.filter((row) => {
              if (!row.dataLog?.submissionDate) return false;
              const submissionDate = new Date(row.dataLog.submissionDate);
              const start = startDate ? new Date(startDate) : null;
              const end = endDate ? new Date(endDate) : null;
              if (start && end) {
                return submissionDate >= start && submissionDate <= end;
              } else if (start) {
                return submissionDate >= start;
              } else if (end) {
                return submissionDate <= end;
              }
              return true;
            });
          }
        } else if (filterKey === 'mappingName' && Array.isArray(filterValue)) {
          filteredData = filteredData.filter((item) => {
            if (!item.mappingName) return false;
            return filterValue.some((val: string) => {
              if (!val || typeof val !== 'string') return false;
              const valLower = val.toLowerCase().trim();
              const mappingNameLower = item.mappingName?.toLowerCase();
              return mappingNameLower && mappingNameLower.includes(valLower);
            });
          });
        } else if (filterKey === 'returnName' && Array.isArray(filterValue)) {
          const normalize = (s: string) =>
            s.toLowerCase().replace(/\s+/g, ' ').trim();
          filteredData = filteredData.filter((item) => {
            return filterValue.some((val: string) => {
              if (!val || typeof val !== 'string') return false;
              const valNorm = normalize(val);
              // 1) Match by mappingName first (same format as URL: "CountryName - Return Name") - no entities needed
              if (item.mappingName) {
                const mappingNorm = normalize(item.mappingName);
                if (
                  mappingNorm === valNorm ||
                  mappingNorm.includes(valNorm) ||
                  valNorm.includes(mappingNorm)
                ) {
                  return true;
                }
              }
              // 2) Match by return type name from entities (exact or contains)
              if (
                item.returnTypeId &&
                entities &&
                Array.isArray(entities) &&
                entities.length > 0
              ) {
                for (const entity of entities) {
                  const returnType = entity.returnTypesEdit?.find(
                    (rt) => rt.id === item.returnTypeId
                  );
                  if (returnType && returnType.name) {
                    const returnTypeNameNorm = normalize(returnType.name);
                    if (
                      returnTypeNameNorm === valNorm ||
                      returnTypeNameNorm.includes(valNorm) ||
                      valNorm.includes(returnTypeNameNorm)
                    ) {
                      return true;
                    }
                  }
                }
              }
              return false;
            });
          });
        } else if (
          (filterKey === 'preparer' ||
            filterKey === 'reviewer' ||
            filterKey === 'client') &&
          Array.isArray(filterValue)
        ) {
          // Role-specific user filter (preparer, reviewer, or client)
          filteredData = filteredData.filter((item) => {
            if (!item.dataLog) return false;
            return filterValue.some((userName: string) => {
              const userNameLower = userName.toLowerCase().trim();

              if (filterKey === 'preparer' && item.dataLog?.preparerUser) {
                const preparerName =
                  `${item.dataLog.preparerUser.firstName || ''} ${
                    item.dataLog.preparerUser.lastName || ''
                  }`.trim() ||
                  item.dataLog.preparerUser.email ||
                  '';
                if (
                  preparerName.toLowerCase() === userNameLower ||
                  preparerName.toLowerCase().includes(userNameLower)
                ) {
                  return true;
                }
              }

              if (filterKey === 'reviewer' && item.dataLog?.reviewerUser) {
                const reviewerName =
                  `${item.dataLog.reviewerUser.firstName || ''} ${
                    item.dataLog.reviewerUser.lastName || ''
                  }`.trim() ||
                  item.dataLog.reviewerUser.email ||
                  '';
                if (
                  reviewerName.toLowerCase() === userNameLower ||
                  reviewerName.toLowerCase().includes(userNameLower)
                ) {
                  return true;
                }
              }

              if (filterKey === 'client' && item.dataLog?.clientUser) {
                const clientName =
                  `${item.dataLog.clientUser.firstName || ''} ${
                    item.dataLog.clientUser.lastName || ''
                  }`.trim() ||
                  item.dataLog.clientUser.email ||
                  '';
                if (
                  clientName.toLowerCase() === userNameLower ||
                  clientName.toLowerCase().includes(userNameLower)
                ) {
                  return true;
                }
              }

              return false;
            });
          });
        }
        // Add more filter types here as needed
      });

      // Apply explicit status exclusions - this must run AFTER all other filters
      // to ensure excluded statuses are never included, regardless of other filter settings
      const excludeStatuses = cardFilterConfig.excludeStatuses;
      if (excludeStatuses && excludeStatuses.length > 0) {
        const excludedStatusesSet = new Set(excludeStatuses);
        filteredData = filteredData.filter((item) => {
          const itemStatus = item.status;
          if (!itemStatus) return true;
          if (excludedStatusesSet.has(itemStatus as DataLogStatus))
            return false;
          for (const excludedStatus of excludeStatuses) {
            if (
              itemStatus === excludedStatus ||
              itemStatus === String(excludedStatus) ||
              (typeof excludedStatus === 'string' &&
                itemStatus.toLowerCase() === excludedStatus.toLowerCase())
            ) {
              return false;
            }
          }
          return true;
        });
      }

      if (isMounted) {
        setDataSource(filteredData);
        cardFilterConfigAppliedRef.current = JSON.stringify(cardFilterConfig);
      }

      // Update UI filter state (for display in filter dropdowns) - do all updates in parallel
      // Use the same filters for list, board, and calendar so all views share one filtering system
      if (isMounted) {
        const uiFiltersState: any = {};
        Object.keys(cardFilterConfig.filters).forEach((filterKey) => {
          // Skip returnName in UI state - table filter pipeline has no returnName handler and would filter out all rows (row.returnName is undefined)
          if (filterKey === 'returnName') return;
          const filter = cardFilterConfig.filters[filterKey];
          // Only set in UI if displayInUI is true (default) or not specified
          if (filter.displayInUI !== false) {
            // For nextDueDate, use computed date range / DateOption when from due-date type (overdue, dueToday, etc.)
            // so the table can display the due date filter correctly
            if (filterKey === 'nextDueDate' && nextDueDateUiValue !== null) {
              uiFiltersState[filterKey] = { value: nextDueDateUiValue };
            } else {
              uiFiltersState[filterKey] = { value: filter.value };
            }
          }
        });
        // Type of Returns sends returnName as "Country - Return Name"; Tax Return column uses mappingName - set both so selection shows
        if (cardFilterConfig.filters.returnName?.value) {
          uiFiltersState.mappingName = {
            value: cardFilterConfig.filters.returnName.value,
          };
        }

        // Set shared filter state (used by list, board, and calendar)
        setSharedFiltersState(uiFiltersState);

        // Set status filter for UI if status filter is in config
        if (
          cardFilterConfig.filters.status &&
          Array.isArray(cardFilterConfig.filters.status.value)
        ) {
          setInitialStatusFilter(cardFilterConfig.filters.status.value);
          setUpdateStatusFilter(true);
        }
        // Ensure due date filter is shown in UI when nextDueDate is in config
        if (cardFilterConfig.filters.nextDueDate) {
          setUpdateDueDateFilter(true);
        }
      }
    }

    return () => {
      isMounted = false;
    };
  }, [cardFilterConfig, mappedReturns, currentUser, entities]); // Add entities to dependencies since returnName filter needs it

  // Apply initial filter from query parameters (legacy system - only if no cardFilterConfig)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;

    // Skip if cardFilterConfig is present (new system takes priority)
    if (cardFilterConfig) {
      return;
    }

    if (initialFilter && mappedReturns) {
      const initialFilterKey = JSON.stringify(initialFilter);
      if (initialFilterAppliedRef.current === initialFilterKey) {
        return;
      }
      const { type, value } = initialFilter;
      let filteredData = mappedReturns;

      switch (type) {
        case 'status':
          if (value) {
            // Handle multiple comma-separated status values
            const statusArray = value.split(',');
            filteredData = mappedReturns.filter((item) =>
              statusArray.includes(item.status)
            );
            if (isMounted) {
              setInitialStatusFilter(statusArray);
              setUpdateStatusFilter(true);
              setSelectedContanier('');
            }
          } else {
            // No status (Not Started) - match the 'Not Started' enum value
            filteredData = mappedReturns.filter(
              (item) => item.status === DataLogStatus.NOT_STARTED
            );
            if (isMounted) {
              setInitialStatusFilter(['NOT_STARTED']);
              setUpdateStatusFilter(true);
              setSelectedContanier('');
            }
          }
          break;
        case 'dueDate':
          const now = new Date();
          const today = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );
          const weekFromNow = new Date(today);
          weekFromNow.setDate(weekFromNow.getDate() + 7);
          const monthFromNow = new Date(today);
          monthFromNow.setMonth(monthFromNow.getMonth() + 1);
          const nextMonthFromNow = new Date(today);
          nextMonthFromNow.setMonth(nextMonthFromNow.getMonth() + 2);

          switch (value) {
            case 'overdue':
              filteredData = mappedReturns.filter((item) => {
                if (!item.nextDueDate) return false;
                // Parse the due date and compare only the date part (ignore time)
                const dueDate = new Date(item.nextDueDate);
                const dueDateOnly = new Date(
                  dueDate.getFullYear(),
                  dueDate.getMonth(),
                  dueDate.getDate()
                );
                const todayOnly = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate()
                );
                return dueDateOnly < todayOnly;
              });
              if (isMounted) {
                setUpdateDueDateFilter(true);
                setSelectedContanier(dashboardStatistic.expiredReturns);
              }
              break;
            case 'dueToday':
              filteredData = mappedReturns.filter((item) => {
                if (!item.nextDueDate) return false;
                const dueDate = new Date(item.nextDueDate);
                return (
                  dueDate.getDate() === today.getDate() &&
                  dueDate.getMonth() === today.getMonth() &&
                  dueDate.getFullYear() === today.getFullYear()
                );
              });
              if (isMounted) {
                setUpdateDueDateFilter(true);
                setSelectedContanier('');
              }
              break;
            case 'dueThisWeek':
              filteredData = mappedReturns.filter((item) => {
                if (!item.nextDueDate) return false;
                const dueDate = new Date(item.nextDueDate);
                return dueDate > today && dueDate <= weekFromNow;
              });
              if (isMounted) {
                setUpdateDueDateFilter(true);
                setSelectedContanier('');
              }
              break;
            case 'dueThisMonth':
              filteredData = mappedReturns.filter((item) => {
                if (!item.nextDueDate) return false;
                const dueDate = new Date(item.nextDueDate);
                return dueDate > weekFromNow && dueDate <= monthFromNow;
              });
              if (isMounted) {
                setUpdateDueDateFilter(true);
                setSelectedContanier('');
              }
              break;
            case 'dueNextMonth':
              filteredData = mappedReturns.filter((item) => {
                if (!item.nextDueDate) return false;
                const dueDate = new Date(item.nextDueDate);
                return dueDate > monthFromNow && dueDate <= nextMonthFromNow;
              });
              if (isMounted) {
                setUpdateDueDateFilter(true);
                setSelectedContanier('');
              }
              break;
            case 'dueLater':
              filteredData = mappedReturns.filter((item) => {
                if (!item.nextDueDate) return false;
                const dueDate = new Date(item.nextDueDate);
                return dueDate > nextMonthFromNow;
              });
              if (isMounted) {
                setUpdateDueDateFilter(true);
                setSelectedContanier('');
              }
              break;
          }

          // Apply person filter if provided (for logged-in user)
          if (initialFilter?.personFilter) {
            const rolePersonPairs = initialFilter.personFilter.split(',');
            filteredData = filteredData.filter((item) => {
              // Include NOT_STARTED items even if they don't have dataLog
              if (item.status === DataLogStatus.NOT_STARTED) {
                return true;
              }
              return rolePersonPairs.some((pair) => {
                const [role, personName] = pair.split(':');
                if (role === 'preparer' && item.dataLog?.preparerUser) {
                  const preparerName =
                    `${item.dataLog.preparerUser.firstName || ''} ${
                      item.dataLog.preparerUser.lastName || ''
                    }`.trim() ||
                    item.dataLog.preparerUser.email ||
                    t('common.unknown');
                  return preparerName === personName;
                } else if (role === 'reviewer' && item.dataLog?.reviewerUser) {
                  const reviewerName =
                    `${item.dataLog.reviewerUser.firstName || ''} ${
                      item.dataLog.reviewerUser.lastName || ''
                    }`.trim() ||
                    item.dataLog.reviewerUser.email ||
                    t('common.unknown');
                  return reviewerName === personName;
                } else if (role === 'client' && item.dataLog?.clientUser) {
                  const clientName =
                    `${item.dataLog.clientUser.firstName || ''} ${
                      item.dataLog.clientUser.lastName || ''
                    }`.trim() ||
                    item.dataLog.clientUser.email ||
                    t('common.unknown');
                  return clientName === personName;
                }
                return false;
              });
            });
          }

          // Apply status filter with automatic exclusion of submitted/cancelled
          filteredData = applyStatusFilterWithExclusion(
            filteredData,
            initialFilter?.statusFilter,
            DEFAULT_EXCLUDED_STATUSES
          );

          // Explicitly exclude SUBMITTED and CANCELLED to ensure they're never shown for dueDate filters
          filteredData = filteredData.filter(
            (item) =>
              item.status !== DataLogStatus.SUBMITTED &&
              item.status !== DataLogStatus.CANCELLED
          );
          // Set status filter in UI if statusFilter was provided
          if (initialFilter?.statusFilter && isMounted) {
            const statusArray = initialFilter.statusFilter
              .split(',')
              .map((s) => s.trim());
            // Ensure SUBMITTED and CANCELLED are not in the status filter array
            const filteredStatusArray = statusArray.filter(
              (status) =>
                status !== DataLogStatus.SUBMITTED &&
                status !== DataLogStatus.CANCELLED
            );
            setInitialStatusFilter(filteredStatusArray);
            setUpdateStatusFilter(true);
          } else if (isMounted) {
            setUpdateStatusFilter(false);
          }

          // Don't set initialFiltersState - apply filters silently without showing in UI
          // This avoids complexity of managing filter state and order
          if (isMounted) {
            setSharedFiltersState({});
          }
          break;
        case 'returnType':
          if (value && entities) {
            // Find returnTypeId that matches the return type name
            let matchingReturnTypeIds: string[] = [];
            entities.forEach((entity) => {
              const returnType = entity.returnTypesEdit?.find(
                (rt) => rt.name === value
              );
              if (returnType && returnType.id) {
                matchingReturnTypeIds.push(returnType.id);
              }
            });

            if (matchingReturnTypeIds.length > 0) {
              // First filter by return type
              filteredData = mappedReturns.filter(
                (item) =>
                  item.returnTypeId &&
                  matchingReturnTypeIds.includes(item.returnTypeId)
              );

              // Apply person filter if provided (for logged-in user)
              if (initialFilter?.personFilter) {
                const rolePersonPairs = initialFilter.personFilter.split(',');
                filteredData = filteredData.filter((item) => {
                  // Include NOT_STARTED items even if they don't have dataLog
                  if (item.status === DataLogStatus.NOT_STARTED) {
                    return true;
                  }
                  return rolePersonPairs.some((pair) => {
                    const [role, personName] = pair.split(':');
                    if (role === 'preparer' && item.dataLog?.preparerUser) {
                      const preparerName =
                        `${item.dataLog.preparerUser.firstName || ''} ${
                          item.dataLog.preparerUser.lastName || ''
                        }`.trim() ||
                        item.dataLog.preparerUser.email ||
                        t('common.unknown');
                      return preparerName === personName;
                    } else if (
                      role === 'reviewer' &&
                      item.dataLog?.reviewerUser
                    ) {
                      const reviewerName =
                        `${item.dataLog.reviewerUser.firstName || ''} ${
                          item.dataLog.reviewerUser.lastName || ''
                        }`.trim() ||
                        item.dataLog.reviewerUser.email ||
                        t('common.unknown');
                      return reviewerName === personName;
                    } else if (role === 'client' && item.dataLog?.clientUser) {
                      const clientName =
                        `${item.dataLog.clientUser.firstName || ''} ${
                          item.dataLog.clientUser.lastName || ''
                        }`.trim() ||
                        item.dataLog.clientUser.email ||
                        t('common.unknown');
                      return clientName === personName;
                    }
                    return false;
                  });
                });
              }

              // Apply status filter with automatic exclusion of submitted/cancelled
              filteredData = applyStatusFilterWithExclusion(
                filteredData,
                initialFilter?.statusFilter,
                DEFAULT_EXCLUDED_STATUSES
              );

              // Explicitly exclude SUBMITTED and CANCELLED to ensure they're never shown for returnType filters
              filteredData = filteredData.filter(
                (item) =>
                  item.status !== DataLogStatus.SUBMITTED &&
                  item.status !== DataLogStatus.CANCELLED
              );
              // Set status filter in UI if statusFilter was provided
              if (initialFilter?.statusFilter && isMounted) {
                const statusArray = initialFilter.statusFilter
                  .split(',')
                  .map((s) => s.trim());
                // Ensure SUBMITTED and CANCELLED are not in the status filter array
                const filteredStatusArray = statusArray.filter(
                  (status) =>
                    status !== DataLogStatus.SUBMITTED &&
                    status !== DataLogStatus.CANCELLED
                );
                setInitialStatusFilter(filteredStatusArray);
                setUpdateStatusFilter(true);
              } else if (isMounted) {
                setUpdateStatusFilter(false);
              }

              // Don't set initialFiltersState - apply filters silently without showing in UI
              // This avoids complexity of managing filter state and order
              // The filtered data is already set in filteredData and will be passed to ScopeTable
              if (isMounted) {
                setSharedFiltersState({});
              }
            } else {
              // If no match found, show empty
              filteredData = [];
            }
            if (isMounted) {
              setUpdateDueDateFilter(false);
              setSelectedContanier('');
            }
          }
          break;
        case 'role':
          // First filter by role
          if (value === 'preparer') {
            filteredData = mappedReturns.filter(
              (item) =>
                item.dataLog?.preparerUser?.userUuid === currentUser?.userUuid
            );
          } else if (value === 'reviewer') {
            filteredData = mappedReturns.filter(
              (item) =>
                item.dataLog?.reviewerUser?.userUuid === currentUser?.userUuid
            );
          }

          // Apply status filter with automatic exclusion of submitted/cancelled
          filteredData = applyStatusFilterWithExclusion(
            filteredData,
            initialFilter?.statusFilter,
            DEFAULT_EXCLUDED_STATUSES
          );

          // Explicitly exclude SUBMITTED and CANCELLED to ensure they're never shown for role filters
          filteredData = filteredData.filter(
            (item) =>
              item.status !== DataLogStatus.SUBMITTED &&
              item.status !== DataLogStatus.CANCELLED
          );

          // Set status filter in UI if statusFilter was provided
          if (initialFilter?.statusFilter && isMounted) {
            const statusArray = initialFilter.statusFilter
              .split(',')
              .map((s) => s.trim());
            // Ensure SUBMITTED and CANCELLED are not in the status filter array
            const filteredStatusArray = statusArray.filter(
              (status) =>
                status !== DataLogStatus.SUBMITTED &&
                status !== DataLogStatus.CANCELLED
            );
            setInitialStatusFilter(filteredStatusArray);
            setUpdateStatusFilter(true);
          } else if (isMounted) {
            setUpdateStatusFilter(false);
          }

          if (isMounted) {
            setUpdateDueDateFilter(false);
            setSelectedContanier('');
            setSelectedPresetFilters([]);
          }
          break;
        case 'submissionDate':
        case 'dataLog.submissionDate':
          if (value) {
            // Handle submission date filter - support DateOptions.THIS_MONTH
            if (value === DateOptions.THIS_MONTH) {
              const now = new Date();
              const currentMonthStart = new Date(
                now.getFullYear(),
                now.getMonth(),
                1
              );
              const currentMonthEnd = new Date(
                now.getFullYear(),
                now.getMonth() + 1,
                0,
                23,
                59,
                59,
                999
              );
              filteredData = mappedReturns.filter((item) => {
                if (!item.dataLog?.submissionDate) return false;
                const submissionDate = new Date(item.dataLog.submissionDate);
                return (
                  submissionDate >= currentMonthStart &&
                  submissionDate <= currentMonthEnd
                );
              });
            } else if (Array.isArray(value) && value.length === 2) {
              // Handle date range array
              const [startDate, endDate] = value;
              filteredData = mappedReturns.filter((item) => {
                if (!item.dataLog?.submissionDate) return false;
                const submissionDate = new Date(item.dataLog.submissionDate);
                const start = startDate ? new Date(startDate) : null;
                const end = endDate ? new Date(endDate) : null;

                if (start && end) {
                  return submissionDate >= start && submissionDate <= end;
                } else if (start) {
                  return submissionDate >= start;
                } else if (end) {
                  return submissionDate <= end;
                }
                return true;
              });
            }

            // Set initialFiltersState so the filter UI shows the submission date filter
            // Use 'dataLog.submissionDate' to match the filter key in ScopeTable
            if (isMounted && value) {
              setSharedFiltersState({
                'dataLog.submissionDate': {
                  value:
                    value === DateOptions.THIS_MONTH
                      ? DateOptions.THIS_MONTH
                      : value,
                },
              });
            }

            if (isMounted) {
              setUpdateDueDateFilter(false);
              setUpdateStatusFilter(false);
              setSelectedContanier('');
            }
          }
          break;
        case 'person':
          if (value) {
            // Support multiple roles separated by commas: "preparer:name,reviewer:name,client:name"
            const rolePersonPairs = value.split(',');
            // Filter the data - user must match in at least one role
            filteredData = mappedReturns.filter((item) => {
              // Include NOT_STARTED items even if they don't have dataLog
              if (item.status === DataLogStatus.NOT_STARTED) {
                return true;
              }
              return rolePersonPairs.some((pair) => {
                const [role, personName] = pair.split(':');
                if (role === 'preparer' && item.dataLog?.preparerUser) {
                  const preparerName =
                    `${item.dataLog.preparerUser.firstName || ''} ${
                      item.dataLog.preparerUser.lastName || ''
                    }`.trim() ||
                    item.dataLog.preparerUser.email ||
                    t('common.unknown');
                  return preparerName === personName;
                } else if (role === 'reviewer' && item.dataLog?.reviewerUser) {
                  const reviewerName =
                    `${item.dataLog.reviewerUser.firstName || ''} ${
                      item.dataLog.reviewerUser.lastName || ''
                    }`.trim() ||
                    item.dataLog.reviewerUser.email ||
                    t('common.unknown');
                  return reviewerName === personName;
                } else if (role === 'client' && item.dataLog?.clientUser) {
                  const clientName =
                    `${item.dataLog.clientUser.firstName || ''} ${
                      item.dataLog.clientUser.lastName || ''
                    }`.trim() ||
                    item.dataLog.clientUser.email ||
                    t('common.unknown');
                  return clientName === personName;
                }
                return false;
              });
            });

            // Apply status filter with automatic exclusion of submitted/cancelled
            filteredData = applyStatusFilterWithExclusion(
              filteredData,
              initialFilter?.statusFilter,
              DEFAULT_EXCLUDED_STATUSES
            );

            // Explicitly exclude SUBMITTED and CANCELLED to ensure they're never shown for person filters
            filteredData = filteredData.filter(
              (item) =>
                item.status !== DataLogStatus.SUBMITTED &&
                item.status !== DataLogStatus.CANCELLED
            );

            // Set status filter in UI if statusFilter was provided
            if (initialFilter?.statusFilter && isMounted) {
              const statusArray = initialFilter.statusFilter
                .split(',')
                .map((s) => s.trim());
              // Ensure SUBMITTED and CANCELLED are not in the status filter array
              const filteredStatusArray = statusArray.filter(
                (status) =>
                  status !== DataLogStatus.SUBMITTED &&
                  status !== DataLogStatus.CANCELLED
              );
              setInitialStatusFilter(filteredStatusArray);
              setUpdateStatusFilter(true);
            } else if (isMounted) {
              setUpdateStatusFilter(false);
            }

            if (isMounted) {
              setUpdateDueDateFilter(false);
              setSelectedContanier('');
            }
            // Set selectedPresetFilters to activate the filter UI
            // For multiple roles, set the first one as the primary filter
            timeoutId = setTimeout(() => {
              if (isMounted) {
                const firstPair = rolePersonPairs[0].split(':');
                setSelectedPresetFilters([
                  {
                    key: firstPair[0],
                    value: firstPair[1],
                  },
                ]);
              }
            }, 0);
          }
          break;
        default:
          // Clear selectedPresetFilters for other filter types
          if (isMounted) {
            setSelectedPresetFilters([]);
          }
          break;
      }

      if (isMounted) {
        setDataSource(filteredData);
        initialFilterAppliedRef.current = initialFilterKey;
      }
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter, mappedReturns]);

  useEffect(() => {
    if (!initialFilter) {
      initialFilterAppliedRef.current = null;
      return;
    }
    const nextKey = JSON.stringify(initialFilter);
    if (
      initialFilterAppliedRef.current &&
      initialFilterAppliedRef.current !== nextKey
    ) {
      initialFilterAppliedRef.current = null;
    }
  }, [initialFilter]);

  const renderOrgLink = (content: string, record: MappedReturn) => {
    const to = listViewOnly
      ? buildPwaEntityTaxReturnsNavigateUrl(record.organisationId)
      : `/entities/organisation/${record.organisationId}`;
    const isGroupProvider = record.providerName?.toLowerCase() === 'group';
    const hasProviderMark =
      Boolean(record.providerName) &&
      (isGroupProvider ||
        Boolean(providerIcons[record.providerName!.toLowerCase()]) ||
        record.providerName!.toLowerCase().includes('receipt'));
    const providerLink =
      record.providerName && record.shortCode
        ? getProviderLink(record.providerName.toLowerCase(), record.shortCode)
        : null;

    const providerMark = isGroupProvider ? (
      <UsergroupAddOutlined
        style={{
          fontSize: 20,
          color: 'var(--color-text-base, #2e2e2e)',
          paddingTop: '4px',
        }}
      />
    ) : (
      <ProviderIcon
        providerName={record.providerName}
        width={25}
        style={{ paddingTop: '4px' }}
      />
    );

    return (
      <Space size={'small'} className="scope-entity-name-row">
        {hasProviderMark &&
          (providerLink ? (
            <a
              href={providerLink}
              target="_blank"
              rel="noreferrer"
              style={{ height: '32px', display: 'block' }}
              onClick={(e) => e.stopPropagation()}
            >
              <ProviderIconWrap>{providerMark}</ProviderIconWrap>
            </a>
          ) : (
            <ProviderIconWrap>{providerMark}</ProviderIconWrap>
          ))}
        <SubmissionEntityName
          key={record.organisationId}
          content={content}
          to={to}
          hint={t('main.click-to-view-details')}
          onNavigate={clearOrganisationDetailsTabStorage}
        />
      </Space>
    );
  };

  const renderTaxReturn = (content) => {
    return (
      <TruncatedText
        style={{
          maxWidth: 200,
          fontSize: 13,
          fontWeight: 400,
          padding: '6px 8px',
          color: 'var(--color-text-base, #2e2e2e)',
        }}
      >
        {localizeReturnLabel(content)}
      </TruncatedText>
    );
  };

  const renderSortIcon = useCallback(({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    } else {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
  }, []);

  const scrollWithOffset = (el) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -300;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
  };

  const renderContextMenu = (record) => {
    const to = `entities/organisation/${record.organisationId}#${record.mappingId}`;
    const isAddSubmissionDisabled =
      currentUser &&
      AccountType[currentUser.accountType] === AccountType.STAFF &&
      hasAdvancedUserRights &&
      !hasAdvancedUserRight(
        currentUser,
        record,
        AdvancedUserRightsCodes.CREATE_SUBMISSION
      );
    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          {!checkLoading(record, DISABLE) && record.connectedProperly && (
            <StyledTooltip // @ts-ignore
              title={
                isAddSubmissionDisabled
                  ? t('global.create-submission-rights-warning')
                  : ''
              }
              placement="top"
              arrow={false}
            >
              <IconContextButton
                type="text"
                icon={
                  <PlusCircleOutlined
                    style={{ width: '18px', height: '22px' }}
                  />
                }
                onClick={() => {
                  vatGenerate(record.connectionId, record.mappingId, undefined);
                }}
                disabled={isAddSubmissionDisabled}
              >
                {t('organisation.add-submission')}
              </IconContextButton>
            </StyledTooltip>
          )}
          <IconContextButton
            type="text"
            icon={<EyeIcon style={{ width: '18px', height: '22px' }} />}
          >
            <HashLink
              smooth
              style={{ textDecoration: 'none' }}
              to={to}
              scroll={(el) => scrollWithOffset(el)}
              onClick={clearOrganisationDetailsTabStorage}
            >
              {t('organisation.view-previous-submission')}
            </HashLink>
          </IconContextButton>
          {!record.preparer &&
            currentUser &&
            AccountType[currentUser.accountType] !== AccountType.CLIENT &&
            currentUser?.canManageOrgs &&
            canEditTaxReturn(
              record.connectedProperly,
              currentUser,
              record.dataLog?.status
            ) && (
              <IconContextButton
                type="text"
                icon={
                  <EditOutlined style={{ width: '18px', height: '18px' }} />
                }
              >
                <Link
                  style={{ textDecoration: 'none' }}
                  to={'#'}
                  onClick={() =>
                    editReturnMapping(record.connectionId, record.mappingId)
                  }
                >
                  {t('organisation.edit-return')}
                </Link>
              </IconContextButton>
            )}
          {record.canDelete && (
            <StyledPopconfirm // @ts-ignore
              title={t('submissions.cancel-return-title')}
              description={t('submissions.cancel-return-description')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={() =>
                deleteReturnMapping(record.organisationId, record.mappingId)
              }
            >
              <IconContextButton
                type="text"
                icon={
                  <DeleteOutlined style={{ width: '18px', height: '18px' }} />
                }
              >
                {t('organisation.cancel-return')}
              </IconContextButton>
            </StyledPopconfirm>
          )}
        </Space>
      </div>
    );
  };

  const renderDueDate = (content, _record: MappedReturn) => {
    if (!content) return null;

    // Use stable format for moment to avoid deprecation (Date or moment object)
    let dueDate: moment.Moment;
    if (content instanceof Date) {
      dueDate = moment(content.toISOString());
    } else if (moment.isMoment(content)) {
      dueDate = moment(content.valueOf());
    } else {
      dueDate = moment(content);
    }
    const currentDate = moment();

    return (
      <span
        className={
          dueDate.valueOf() < currentDate.valueOf() ? 'expired-due-date' : ''
        }
      >
        {formatDisplayDate(dueDate, 'DD/MM/YYYY', i18n.language)}
      </span>
    );
  };

  const calcTaxReturns = () => {
    return mappedReturns?.filter(
      (item) => item.status !== DataLogStatus.SUBMITTED
    )?.length;
  };

  const calcDueReturns = () => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0
    ).getTime();
    const endOfDay30 = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 30,
      23,
      59,
      59,
      999
    ).getTime();
    return mappedReturns?.filter((item) => {
      if (item.status === DataLogStatus.SUBMITTED) return false;
      const due = new Date(item?.nextDueDate || 0).getTime();
      return due >= startOfToday && due <= endOfDay30;
    })?.length;
  };

  const calcExpiredReturns = () => {
    return mappedReturns?.filter((item) => {
      //@ts-ignore
      const isOverdue =
        new Date(item?.nextDueDate || '').valueOf() < new Date().valueOf();
      // Exclude submitted returns only; include Not Started
      return isOverdue && item.status !== DataLogStatus.SUBMITTED;
    })?.length;
  };

  const calcAwaitingClientReturns = () => {
    return mappedReturns?.filter(
      (item) => item.status == DataLogStatus.AWAITING_CLIENT_APPROVAL
    )?.length;
  };

  /** Returns filter state for the list-tab dashboard boxes so the filter bar shows the same filters as the box selection. */
  const getFiltersStateForDashboardBox = (
    key: string
  ): Record<string, { value: any }> => {
    const allStatuses = Object.values(DataLogStatus);
    switch (key) {
      case dashboardStatistic.taxReturns:
        return {
          status: {
            value: allStatuses.filter((s) => s !== DataLogStatus.SUBMITTED),
          },
        };
      case dashboardStatistic.dueReturns: {
        const now = new Date();
        const startOfToday = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0,
          0,
          0,
          0
        );
        const endOfDay30 = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() + 30,
          23,
          59,
          59,
          999
        );
        return {
          nextDueDate: {
            value: [startOfToday.toISOString(), endOfDay30.toISOString()] as [
              string,
              string
            ],
          },
          status: {
            value: allStatuses.filter((s) => s !== DataLogStatus.SUBMITTED),
          },
        };
      }
      case dashboardStatistic.expiredReturns: {
        const now = new Date();
        const endOfYesterday = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 1,
          23,
          59,
          59,
          999
        );
        return {
          nextDueDate: {
            value: ['', endOfYesterday.toISOString()] as [string, string],
          },
          status: {
            value: allStatuses.filter((s) => s !== DataLogStatus.SUBMITTED),
          },
        };
      }
      case dashboardStatistic.awaitingClient:
        return {
          status: {
            value: [DataLogStatus.AWAITING_CLIENT_APPROVAL],
          },
        };
      default:
        return {};
    }
  };

  const filterDueReturns = (key) => {
    setSelectedContanier(key);
    setSelectedViewId(null);
    const filtersState = getFiltersStateForDashboardBox(key);
    setSharedFiltersState(filtersState);
    const statusValue = filtersState.status?.value;
    const statusValues =
      statusValue != null
        ? Array.isArray(statusValue)
          ? statusValue
          : [statusValue]
        : null;
    if (statusValues?.length) {
      setStatusFilterFromView(statusValues);
      setInitialStatusFilter(statusValues);
      setUpdateStatusFilter(true);
    } else {
      setStatusFilterFromView(null);
      setInitialStatusFilter([]);
      setUpdateStatusFilter(false);
    }
    const hasDueDateFilter = filtersState.nextDueDate != null;
    setUpdateDueDateFilter(!!hasDueDateFilter);
    // Apply against full mappedReturns; keep override null so later filter
    // dropdown changes keep working on the full dataset.
    setCardOverrideSource(null);
  };

  // PWA Tax Returns: default to the same view as tapping the "Tax Returns" summary card
  // (all non-submitted). Only when there is no URL/card filter already in play.
  const pwaDefaultTaxReturnsAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (!listViewOnly) return;
    if (pwaDefaultTaxReturnsAppliedRef.current) return;
    if (initialFilter || cardFilterConfig) return;
    if (!mappedReturns?.length) return;
    pwaDefaultTaxReturnsAppliedRef.current = true;
    filterDueReturns(dashboardStatistic.taxReturns);
    // Intentionally run once when returns first load on PWA list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listViewOnly, mappedReturns, initialFilter, cardFilterConfig]);

  const checkReconnectedBtn = (record: MappedReturn) => {
    if (!record.dataLog && !record.connectedProperly) {
      return true;
    }
    return false;
  };

  const checkLoading = (record: MappedReturn, item: string) => {
    let load = false;
    let dis = false;

    if (!record.dataLog && !record.connectedProperly && gettingTaxes) {
      dis = true;
      load = true;
    }

    if (item === BUTTON_TEXT) {
      return getReturnActionLabel(record, t, currentUser);
    }
    if (item === LOADING) return load;
    if (item === DISABLE) return dis;
  };

  const checkForClient = (record: MappedReturn) => {
    if (
      currentUser?.accountType &&
      AccountType[currentUser?.accountType] === AccountType.CLIENT
    ) {
      if (
        record.status === DataLogStatus.AWAITING_CLIENT_APPROVAL ||
        record.status === DataLogStatus.CLIENT_APPROVED ||
        record.status === DataLogStatus.CANCELLED ||
        record.status === DataLogStatus.LOCKED ||
        record.status === DataLogStatus.SUBMITTED
      )
        return true;
      else return false;
    } else return true;
  };

  const getPeriodDisplay = (record: MappedReturn) => {
    const locale = i18n.language;
    const isNotStarted = record.status === DataLogStatus.NOT_STARTED;
    const hasDates = !!record.fromReportingDate || !!record.toReportingDate;

    // If NOT_STARTED and no reporting dates, fall back to nextPeriod
    if (isNotStarted && !hasDates) {
      return record.nextPeriod
        ? localizeDigitsForLocale(record.nextPeriod, locale)
        : '';
    }

    return formatDisplayDateRange(
      record.fromReportingDate,
      record.toReportingDate,
      'DD/MM/YY',
      locale
    );
  };

  const renderPreparer = (record: MappedReturn) => {
    if (
      currentUser &&
      AccountType[currentUser.accountType] === AccountType.CLIENT
    )
      return null;
    if (record.dataLog?.preparerUser) {
      const userId = record.dataLog.preparerUser.userUuid;
      const firstName = record.dataLog.preparerUser.firstName;
      const lastName = record.dataLog.preparerUser.lastName;
      const userPic = record.dataLog.preparerUser?.profilePicture;
      return renderAvatar(
        firstName,
        lastName,
        AvatarSizes.DEFAULT,
        userId,
        userPic
      );
    }
    return null;
  };

  const renderReviewer = (record: MappedReturn) => {
    if (
      currentUser &&
      AccountType[currentUser.accountType] === AccountType.CLIENT
    )
      return null;
    if (record.dataLog?.reviewerUser) {
      const userId = record.dataLog.reviewerUser.userUuid;
      const firstName = record.dataLog.reviewerUser?.firstName;
      const lastName = record.dataLog.reviewerUser?.lastName;
      const userPic = record.dataLog.reviewerUser?.profilePicture;
      return renderAvatar(
        firstName,
        lastName,
        AvatarSizes.DEFAULT,
        userId,
        userPic
      );
    }
    return null;
  };

  const renderClientUser = (record: MappedReturn) => {
    if (isClientLocked) {
      return null;
    }
    if (record.dataLog?.clientUser) {
      const userId = record.dataLog.clientUser.userUuid;
      const firstName = record.dataLog.clientUser?.firstName;
      const lastName = record.dataLog.clientUser?.lastName;
      const userPic = record.dataLog.clientUser?.profilePicture;
      return renderAvatar(
        firstName,
        lastName,
        AvatarSizes.DEFAULT,
        userId,
        userPic
      );
    }
    return null;
  };

  const renderStatus = (content: string) => {
    return <StatusGetter status={content} noDraft />;
  };

  const renderAction = (record: MappedReturn) => {
    const onClick = () => {
      vatGenerate(record.connectionId, record.mappingId, record.dataLog?.uuid);
    };
    return (
      record.mappingId &&
      checkForClient(record) && (
        <Space>
          {checkReconnectedBtn(record) ? (
            <IconButton
              type="default"
              style={{
                height: '25px',
                minWidth: '88.2px',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                padding: '14px 8px',
              }}
              icon={<RefreshIcon width={'18px'} height={'18px'} />}
              onClick={connectEntity}
              disabled={
                (!record.dataLog &&
                  !record.connectedProperly &&
                  gettingTaxes) ||
                !currentUser?.canManageOrgs
              }
              loading={
                !record.dataLog && !record.connectedProperly && gettingTaxes
              }
            >
              {t('common.reconnect')}
            </IconButton>
          ) : (
            <StyledTooltip // @ts-ignore
              title={
                shouldCreateNewSubmission(record) &&
                currentUser &&
                AccountType[currentUser.accountType] === AccountType.STAFF &&
                hasAdvancedUserRights &&
                !hasAdvancedUserRight(
                  currentUser,
                  record,
                  AdvancedUserRightsCodes.CREATE_SUBMISSION
                )
                  ? t('global.create-submission-rights-warning')
                  : ''
              }
              placement="top"
              arrow={false}
            >
              <ScopeButton //@ts-ignore
                width={RETURN_ACTION_BUTTON_WIDTH}
                height="25px"
                fontSize="12px"
                lineheight="12px"
                onClick={onClick}
                type={
                  isReturnActionPrimary(record, currentUser)
                    ? 'primary'
                    : 'default'
                }
                disabled={
                  shouldCreateNewSubmission(record) &&
                  currentUser &&
                  AccountType[currentUser.accountType] ===
                    AccountType.STAFF &&
                  hasAdvancedUserRights &&
                  !hasAdvancedUserRight(
                    currentUser,
                    record,
                    AdvancedUserRightsCodes.CREATE_SUBMISSION
                  )
                }
                //@ts-ignore
                loading={checkLoading(record, LOADING)}
              >
                {checkLoading(record, BUTTON_TEXT)}
              </ScopeButton>
            </StyledTooltip>
          )}
        </Space>
      )
    );
  };

  const renderMenu = (record: MappedReturn) => {
    if (
      !(
        record.mappingId &&
        checkForClient(record) &&
        currentUser?.accountType &&
        AccountType[currentUser.accountType] !== AccountType.CLIENT
      )
    ) {
      return null;
    }
    const menuKey = submissionMenuKey(record);
    const menuOpen = openMenuKey === menuKey;
    const menuButton = (
      <ContextButton
        type="text"
        aria-label={t('submissions.actions-header')}
        onClick={(e) => {
          e?.stopPropagation?.();
          if (!menuOpen) setOpenMenuKey(menuKey);
        }}
      >
        <EllipseIcon
          style={{
            width: 18,
            height: 18,
          }}
        />
      </ContextButton>
    );
    if (!menuOpen) return menuButton;
    return (
      <Popover
        placement="topLeft"
        arrow={false}
        trigger="click"
        open
        content={renderContextMenu(record)}
        title={''}
        overlayStyle={{ zIndex: 999 }}
        destroyOnHidden={true}
        onOpenChange={(open) => setOpenMenuKey(open ? menuKey : null)}
      >
        {menuButton}
      </Popover>
    );
  };

  submissionsCellApi.renderTaxReturn = renderTaxReturn;
  submissionsCellApi.renderOrgLink = renderOrgLink;
  submissionsCellApi.renderPeriod = getPeriodDisplay;
  submissionsCellApi.renderDueDate = renderDueDate;
  submissionsCellApi.renderPreparer = renderPreparer;
  submissionsCellApi.renderReviewer = renderReviewer;
  submissionsCellApi.renderClient = renderClientUser;
  submissionsCellApi.renderStatus = renderStatus;
  submissionsCellApi.renderAction = renderAction;
  submissionsCellApi.renderMenu = renderMenu;

  const sameRecord = (next: MappedReturn, prev: MappedReturn) => next !== prev;

  const columns = useMemo(
    () => [
    {
      title: t('main.taxReturn'),
      dataIndex: 'mappingName',
      key: 'mappingName',
      width: 220,
      render: (content: string) => (
        <SubmissionsTaxReturnCell content={content} />
      ),
      shouldCellUpdate: sameRecord,
      sorter: (a, b) => a.mappingName.localeCompare(b.mappingName),
      sortIcon: renderSortIcon,
    },
    {
      title: t('common.entity'),
      dataIndex: 'organisation',
      key: 'organisation',
      className: 'scope-submissions-entity-col',
      render: (content: string, record: MappedReturn) => (
        <SubmissionsOrgCell content={content} record={record} />
      ),
      shouldCellUpdate: sameRecord,
      sorter: (a, b) => a.organisation.localeCompare(b.organisation),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.period'),
      dataIndex: 'nextPeriod',
      key: 'nextPeriod',
      width: 160,
      render: (_: any, record: MappedReturn) => (
        <SubmissionsPeriodCell record={record} />
      ),
      shouldCellUpdate: sameRecord,
      sorter: (a, b) =>
        getPeriodDisplay(a as MappedReturn).localeCompare(
          getPeriodDisplay(b as MappedReturn)
        ),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.nextDueDate'),
      dataIndex: 'nextDueDate',
      key: 'nextDueDate',
      width: 140,
      render: (content, record: MappedReturn) => (
        <SubmissionsDueDateCell content={content} record={record} />
      ),
      shouldCellUpdate: sameRecord,
      sorter: (a, b) =>
        new Date(a.nextDueDate).valueOf() - new Date(b.nextDueDate).valueOf(),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.preparer'),
      dataIndex: 'preparer',
      key: 'preparer',
      width: 90,
      sorter: (a, b) =>
        a.dataLog?.preparerUser?.firstName.localeCompare(
          b.dataLog?.preparerUser?.firstName
        ),
      sortIcon: renderSortIcon,
      render: (_content, record: MappedReturn) => (
        <SubmissionsPreparerCell record={record} />
      ),
      shouldCellUpdate: sameRecord,
      responsive: ['lg'],
    },
    {
      title: t('main.reviewer'),
      dataIndex: 'reviewer',
      key: 'reviewer',
      width: 90,
      sorter: (a, b) =>
        a.dataLog?.reviewerUser?.firstName.localeCompare(
          b.dataLog?.reviewerUser?.firstName
        ),
      sortIcon: renderSortIcon,
      render: (_content, record: MappedReturn) => (
        <SubmissionsReviewerCell record={record} />
      ),
      shouldCellUpdate: sameRecord,
      responsive: ['lg'],
    },
    {
      title: t('main.client'),
      dataIndex: 'client',
      key: 'client',
      width: 90,
      sorter: (a, b) =>
        a.dataLog?.clientUser?.firstName.localeCompare(
          b.dataLog?.clientUser?.firstName
        ),
      sortIcon: renderSortIcon,
      render: (_content, record: MappedReturn) => (
        <SubmissionsClientCell record={record} />
      ),
      shouldCellUpdate: sameRecord,
      responsive: ['lg'],
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (content) => <SubmissionsStatusCell content={content} />,
      shouldCellUpdate: sameRecord,
      sorter: (a, b) => a.status?.localeCompare(b.status),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.action-required'),
      width: 150,
      render: (_content, record: MappedReturn) => (
        <SubmissionsActionCell record={record} />
      ),
      shouldCellUpdate: sameRecord,
    },
    {
      title: '',
      key: 'menu',
      width: 50,
      align: 'right' as const,
      render: (_content, record: MappedReturn) => (
        <SubmissionsMenuCell record={record} />
      ),
      shouldCellUpdate: sameRecord,
    },
  ],
    [t, i18n.language, renderSortIcon]
  );
  const savePageSize = (pageSize: number) => {
    handlePreferences(PreferenceActions.SET, {
      name: PAGE_SIZE_MAIN,
      value: pageSize,
    });
  };

  const [hasScrollbar, setHasScrollbar] = React.useState(false);
  const scrollbarCheckTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  React.useEffect(() => {
    const checkScrollbar = () => {
      if (scrollbarCheckTimeoutRef.current)
        clearTimeout(scrollbarCheckTimeoutRef.current);
      scrollbarCheckTimeoutRef.current = setTimeout(() => {
        scrollbarCheckTimeoutRef.current = null;
        const hasVerticalScrollbar =
          document.documentElement.scrollHeight >
          document.documentElement.clientHeight;
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        setHasScrollbar(hasVerticalScrollbar && scrollbarWidth > 0);
      }, 100);
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    // Do NOT listen to 'scroll' - causes setState on every scroll → re-renders → janky/endless scrolling
    // (especially after upload when content updates; resize + ResizeObserver handle content changes)
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (document.body) {
      resizeObserver.observe(document.body);
    }

    const timeoutId = setTimeout(checkScrollbar, 500);

    return () => {
      window.removeEventListener('resize', checkScrollbar);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
      if (scrollbarCheckTimeoutRef.current) {
        clearTimeout(scrollbarCheckTimeoutRef.current);
        scrollbarCheckTimeoutRef.current = null;
      }
    };
  }, [dataSource, loading]);

  const renderReturnActionButton = useCallback(
    (record: MappedReturn) => {
      const onClick = () => {
        vatGenerate(
          record.connectionId,
          record.mappingId,
          record.dataLog?.uuid
        );
      };

      if (!record.mappingId || !checkForClient(record)) {
        return null;
      }

      if (
        !shouldShowReturnAction(record, {
          allowCreate: !listViewOnly,
        })
      ) {
        return null;
      }

      if (checkReconnectedBtn(record)) {
        return (
          <IconButton
            type="default"
            style={{
              height: '28px',
              minWidth: '88px',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              padding: '14px 8px',
            }}
            icon={<RefreshIcon width={'18px'} height={'18px'} />}
            onClick={connectEntity}
            disabled={
              (!record.dataLog &&
                !record.connectedProperly &&
                gettingTaxes) ||
              !currentUser?.canManageOrgs
            }
            loading={
              !record.dataLog && !record.connectedProperly && gettingTaxes
            }
          >
            {t('common.reconnect')}
          </IconButton>
        );
      }

      return (
        <StyledTooltip // @ts-ignore
          title={
            shouldCreateNewSubmission(record) &&
            currentUser &&
            AccountType[currentUser.accountType] === AccountType.STAFF &&
            hasAdvancedUserRights &&
            !hasAdvancedUserRight(
              currentUser,
              record,
              AdvancedUserRightsCodes.CREATE_SUBMISSION
            )
              ? t('global.create-submission-rights-warning')
              : ''
          }
          placement="top"
          arrow={false}
        >
          <ScopeButton //@ts-ignore
            width={listViewOnly ? '100%' : RETURN_ACTION_BUTTON_WIDTH}
            height="28px"
            fontSize="12px"
            lineheight="12px"
            onClick={onClick}
            type={
              isReturnActionPrimary(record, currentUser)
                ? 'primary'
                : 'default'
            }
            disabled={
              shouldCreateNewSubmission(record) &&
              currentUser &&
              AccountType[currentUser.accountType] === AccountType.STAFF &&
              hasAdvancedUserRights &&
              !hasAdvancedUserRight(
                currentUser,
                record,
                AdvancedUserRightsCodes.CREATE_SUBMISSION
              )
            }
            //@ts-ignore
            loading={checkLoading(record, LOADING)}
          >
            {checkLoading(record, BUTTON_TEXT)}
          </ScopeButton>
        </StyledTooltip>
      );
    },
    [
      BUTTON_TEXT,
      LOADING,
      checkForClient,
      checkLoading,
      checkReconnectedBtn,
      connectEntity,
      currentUser,
      gettingTaxes,
      hasAdvancedUserRights,
      listViewOnly,
      t,
      vatGenerate,
    ]
  );

  const renderPwaSubmissionsList = useCallback(() => {
    const rows = displayReturns ?? [];
    const pageSize = pageSizeSaved ? Number(pageSizeSaved) : 10;
    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(pwaListPage, totalPages);
    const start = (safePage - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);
    const rangeStart = total === 0 ? 0 : start + 1;
    const rangeEnd = Math.min(start + pageSize, total);

    if (total === 0) {
      return (
        <div className="scope-pwa-submissions-cards__empty">
          {t('submissions.no-submissions-to-display')}
        </div>
      );
    }

    return (
      <div className="scope-pwa-submissions-cards" ref={pwaSubmissionsListRef}>
        {pageRows.map((record) => (
          <div
            key={`${record.mappingId}-${record.dataLog?.uuid ?? record.organisationId}`}
            className="scope-pwa-submission-card scope-pwa-submission-card--global"
          >
            <div className="scope-pwa-submission-card__body">
              <div className="scope-pwa-submission-card__header">
                {record.providerName ? (
                  <span className="scope-pwa-submission-card__provider">
                    {record.providerName.toLowerCase() === 'group' ? (
                      <UsergroupAddOutlined
                        style={{
                          fontSize: 22,
                          color: 'var(--color-text-base, #2e2e2e)',
                        }}
                        aria-label={record.providerName}
                      />
                    ) : (
                      <ProviderIcon
                        providerName={record.providerName}
                        alt={record.providerName}
                        width={25}
                      />
                    )}
                  </span>
                ) : null}
                <div className="scope-pwa-submission-card__header-text">
                  <div className="scope-pwa-submission-card__title">
                    {localizeReturnLabel(record.mappingName)}
                  </div>
                  <div className="scope-pwa-submission-card__entity">
                    {record.organisation}
                  </div>
                </div>
              </div>
              <div className="scope-pwa-submission-card__period">
                {getPeriodDisplay(record)}
              </div>
              <div className="scope-pwa-submission-card__meta">
                {record.nextDueDate ? (
                  <>
                    {t('main.nextDueDate')}: {renderDueDate(record.nextDueDate, record)}
                  </>
                ) : null}
              </div>
              <div className="scope-pwa-submission-card__status">
                <StatusGetter status={record.status} noDraft />
              </div>
            </div>
            <div className="scope-pwa-submission-card__actions">
              {renderReturnActionButton(record)}
            </div>
          </div>
        ))}
        <div className="scope-pwa-submissions-cards__footer">
          <span className="scope-pwa-submissions-cards__summary">
            {formatPaginationSummaryText(
              t,
              i18n.language,
              [rangeStart, rangeEnd],
              total
            )}
          </span>
          {totalPages > 1 ? (
            <div className="scope-pwa-submissions-cards__pager">
              <ScopeButton
                type="default"
                width="auto"
                height="28px"
                fontSize="12px"
                lineheight="12px"
                disabled={safePage <= 1}
                onClick={() => goToPwaListPage(Math.max(1, safePage - 1))}
              >
                {t('main.previous')}
              </ScopeButton>
              <ScopeButton
                type="default"
                width="auto"
                height="28px"
                fontSize="12px"
                lineheight="12px"
                disabled={safePage >= totalPages}
                onClick={() =>
                  goToPwaListPage(Math.min(totalPages, safePage + 1))
                }
              >
                {t('main.next')}
              </ScopeButton>
            </div>
          ) : null}
        </div>
      </div>
    );
  }, [
    displayReturns,
    getPeriodDisplay,
    goToPwaListPage,
    i18n.language,
    localizeReturnLabel,
    pageSizeSaved,
    pwaListPage,
    renderDueDate,
    renderReturnActionButton,
    t,
  ]);

  const handleSharedFiltersChange = React.useCallback((nextState: any) => {
    // User-driven filter changes must run against the full dataset. Leaving a
    // previous card/URL override (e.g. Submitted-only) makes later Status /
    // Entity picks look like they "don't work" until a summary card is clicked.
    setCardOverrideSource(null);
    setSharedFiltersState(nextState);
  }, []);

  const renderSharedFilters = useCallback(() => {
    if (!mappedReturns || mappedReturns.length === 0) return null;
    const filters = (
      <ScopeTableFilters
        key={`shared-filters-${clearFiltersKey}`}
        filters={ALL_AVAILABLE_FILTERS}
        visibleFilters={visibleFilters}
        value={sharedFiltersState}
        onChange={handleSharedFiltersChange}
        originalDataSource={mappedReturns}
        tableDataSource={displayReturns}
        cardFilterConfig={cardFilterConfig}
        onOpenFilterSelection={handleOpenFilterSelection}
        updateDueDateFilter={updateDueDateFilter}
        setUpdateDueDateFilter={setUpdateDueDateFilter}
        updateStatusFilter={updateStatusFilter}
        setUpdateStatusFilter={setUpdateStatusFilter}
        initialStatusFilter={
          statusFilterFromView !== null
            ? statusFilterFromView
            : initialStatusFilter
        }
        onClearFilters={clearAllFiltersAndViews}
        preserveUserOnClear={!onClearFiltersNavigation}
        setSelectedContanier={setSelectedContanier}
        selectedContanier={selectedContanier}
        selectedPresetFilters={selectedPresetFilters}
        embedded={false}
        filterRowMarginBottom={viewMode === 'list' ? 6 : 32}
        matchTriggerWidth={listViewOnly}
      />
    );
    if (!listViewOnly) {
      return filters;
    }
    return <div className="scope-pwa-submissions-filters">{filters}</div>;
  }, [
    mappedReturns,
    visibleFilters,
    sharedFiltersState,
    displayReturns,
    cardFilterConfig,
    handleOpenFilterSelection,
    updateDueDateFilter,
    setUpdateDueDateFilter,
    updateStatusFilter,
    setUpdateStatusFilter,
    statusFilterFromView,
    initialStatusFilter,
    clearAllFiltersAndViews,
    onClearFiltersNavigation,
    setSelectedContanier,
    selectedContanier,
    selectedPresetFilters,
    clearFiltersKey,
    viewMode,
    listViewOnly,
    handleSharedFiltersChange,
  ]);

  // Build tab items with their content
  const tabItems = useMemo(() => {
    const renderListContent = () => (
      <>
        {viewMode === 'list' && (
          listViewOnly ? (
            <div
              className="scope-pwa-submissions-summary scope-pwa-submissions-summary--meta"
              aria-label={t('main.tax-returns')}
            >
              {loading ? (
                <div className="scope-pwa-submissions-meta">
                  <div className="scope-pwa-submissions-meta__header">
                    <Skeleton.Avatar active size="small" shape="square" />
                    <div className="scope-pwa-submissions-meta__header-text">
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 120 }}
                      />
                    </div>
                  </div>
                  <div className="scope-pwa-submissions-meta__grid">
                    {[0, 1, 2, 3].map((key) => (
                      <div
                        key={key}
                        className="scope-pwa-submissions-meta__cell"
                      >
                        <Skeleton.Avatar active size="small" shape="square" />
                        <div className="scope-pwa-submissions-meta__cell-text">
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: 72 }}
                          />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: 36 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="scope-pwa-submissions-meta">
                  <div className="scope-pwa-submissions-meta__header">
                    <span className="scope-pwa-submissions-meta__icon-wrap" aria-hidden>
                      {selectedContanier === dashboardStatistic.dueReturns ? (
                        <ClockCircleOutlined className="scope-pwa-submissions-meta__icon" />
                      ) : selectedContanier ===
                        dashboardStatistic.expiredReturns ? (
                        <ExclamationCircleOutlined className="scope-pwa-submissions-meta__icon" />
                      ) : selectedContanier ===
                        dashboardStatistic.awaitingClient ? (
                        <TeamOutlined className="scope-pwa-submissions-meta__icon" />
                      ) : (
                        <FileTextOutlined className="scope-pwa-submissions-meta__icon" />
                      )}
                    </span>
                    <div className="scope-pwa-submissions-meta__header-text">
                      <span className="scope-pwa-submissions-meta__label">
                        {selectedContanier === dashboardStatistic.dueReturns
                          ? t('main.due-returns')
                          : selectedContanier ===
                              dashboardStatistic.expiredReturns
                            ? t('main.expired-returns')
                            : selectedContanier ===
                                dashboardStatistic.awaitingClient
                              ? t('main.awaiting-client')
                              : t('main.tax-returns')}
                      </span>
                      <span className="scope-pwa-submissions-meta__header-value">
                        <LocalizedNumber
                          value={
                            selectedContanier === dashboardStatistic.dueReturns
                              ? calcDueReturns()
                              : selectedContanier ===
                                  dashboardStatistic.expiredReturns
                                ? calcExpiredReturns()
                                : selectedContanier ===
                                    dashboardStatistic.awaitingClient
                                  ? calcAwaitingClientReturns()
                                  : calcTaxReturns()
                          }
                        />
                      </span>
                    </div>
                  </div>
                  <div className="scope-pwa-submissions-meta__grid">
                    <button
                      type="button"
                      className={`scope-pwa-submissions-meta__cell${
                        selectedContanier === dashboardStatistic.taxReturns
                          ? ' scope-pwa-submissions-meta__cell--selected'
                          : ''
                      }`}
                      onClick={() => filterDueReturns('tax-returns')}
                    >
                      <span
                        className="scope-pwa-submissions-meta__icon-wrap"
                        aria-hidden
                      >
                        <FileTextOutlined className="scope-pwa-submissions-meta__icon" />
                      </span>
                      <div className="scope-pwa-submissions-meta__cell-text">
                        <span className="scope-pwa-submissions-meta__label">
                          {t('main.tax-returns')}
                        </span>
                        <span className="scope-pwa-submissions-meta__value">
                          <LocalizedNumber value={calcTaxReturns()} />
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`scope-pwa-submissions-meta__cell${
                        selectedContanier === dashboardStatistic.dueReturns
                          ? ' scope-pwa-submissions-meta__cell--selected'
                          : ''
                      }`}
                      onClick={() => filterDueReturns('due-returns')}
                    >
                      <span
                        className="scope-pwa-submissions-meta__icon-wrap"
                        aria-hidden
                      >
                        <ClockCircleOutlined className="scope-pwa-submissions-meta__icon" />
                      </span>
                      <div className="scope-pwa-submissions-meta__cell-text">
                        <span className="scope-pwa-submissions-meta__label">
                          {t('main.due-returns')}
                        </span>
                        <span className="scope-pwa-submissions-meta__value">
                          <LocalizedNumber value={calcDueReturns()} />
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`scope-pwa-submissions-meta__cell${
                        selectedContanier === dashboardStatistic.expiredReturns
                          ? ' scope-pwa-submissions-meta__cell--selected'
                          : ''
                      }`}
                      onClick={() => filterDueReturns('expired-returns')}
                    >
                      <span
                        className="scope-pwa-submissions-meta__icon-wrap"
                        aria-hidden
                      >
                        <ExclamationCircleOutlined className="scope-pwa-submissions-meta__icon" />
                      </span>
                      <div className="scope-pwa-submissions-meta__cell-text">
                        <span className="scope-pwa-submissions-meta__label">
                          {t('main.expired-returns')}
                        </span>
                        <span className="scope-pwa-submissions-meta__value">
                          <LocalizedNumber value={calcExpiredReturns()} />
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`scope-pwa-submissions-meta__cell${
                        selectedContanier === dashboardStatistic.awaitingClient
                          ? ' scope-pwa-submissions-meta__cell--selected'
                          : ''
                      }`}
                      onClick={() => filterDueReturns('awaiting-client')}
                    >
                      <span
                        className="scope-pwa-submissions-meta__icon-wrap"
                        aria-hidden
                      >
                        <TeamOutlined className="scope-pwa-submissions-meta__icon" />
                      </span>
                      <div className="scope-pwa-submissions-meta__cell-text">
                        <span className="scope-pwa-submissions-meta__label">
                          {t('main.awaiting-client')}
                        </span>
                        <span className="scope-pwa-submissions-meta__value">
                          <LocalizedNumber
                            value={calcAwaitingClientReturns()}
                          />
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '25px',
            }}
          >
            {loading ? (
              <>
                <DashboardContainer className="non-selected-container">
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <Skeleton.Avatar active size="large" shape="square" />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '40px' }}
                    />
                  </Space>
                </DashboardContainer>
                <DashboardContainer className="non-selected-container">
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <Skeleton.Avatar active size="large" shape="square" />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '40px' }}
                    />
                  </Space>
                </DashboardContainer>
                <DashboardContainer className="non-selected-container">
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <Skeleton.Avatar active size="large" shape="square" />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '40px' }}
                    />
                  </Space>
                </DashboardContainer>
                <DashboardContainer className="non-selected-container">
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <Skeleton.Avatar active size="large" shape="square" />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '80px' }}
                    />
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: '40px' }}
                    />
                  </Space>
                </DashboardContainer>
              </>
            ) : (
              <>
                <DashboardContainer
                  className={
                    selectedContanier == dashboardStatistic.taxReturns
                      ? 'selected-container'
                      : 'non-selected-container'
                  }
                  onClick={() => filterDueReturns('tax-returns')}
                >
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <ConnectIcon className="container-icon" />
                    <Text className="container-title">
                      {t('main.tax-returns')}
                    </Text>
                    <LocalizedNumber
                      className="container-number"
                      value={calcTaxReturns()}
                    />
                  </Space>
                </DashboardContainer>
                <DashboardContainer
                  className={
                    selectedContanier == dashboardStatistic.dueReturns
                      ? 'selected-container'
                      : 'non-selected-container'
                  }
                  onClick={() => filterDueReturns('due-returns')}
                >
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <ClockIcon className="container-icon" />
                    <Text className="container-title">
                      {t('main.due-returns')}
                    </Text>
                    <LocalizedNumber
                      className="container-number"
                      value={calcDueReturns()}
                    />
                  </Space>
                </DashboardContainer>
                <DashboardContainer
                  className={
                    selectedContanier == dashboardStatistic.expiredReturns
                      ? 'selected-container'
                      : 'non-selected-container'
                  }
                  onClick={() => filterDueReturns('expired-returns')}
                >
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <WarningIcon className="container-icon" />
                    <Text className="container-title">
                      {t('main.expired-returns')}
                    </Text>
                    <LocalizedNumber
                      className="container-number"
                      value={calcExpiredReturns()}
                    />
                  </Space>
                </DashboardContainer>
                <DashboardContainer
                  className={
                    selectedContanier == dashboardStatistic.awaitingClient
                      ? 'selected-container'
                      : 'non-selected-container'
                  }
                  onClick={() => filterDueReturns('awaiting-client')}
                >
                  <Space
                    direction="vertical"
                    style={{ width: '100%', rowGap: 6 }}
                  >
                    <UserIcon className="container-icon" />
                    <Text className="container-title">
                      {t('main.awaiting-client')}
                    </Text>
                    <LocalizedNumber
                      className="container-number"
                      value={calcAwaitingClientReturns()}
                    />
                  </Space>
                </DashboardContainer>
              </>
            )}
          </div>
          )
        )}
        {!loading && (
          <div
            className={
              listViewOnly ? 'scope-pwa-submissions-toolbar' : undefined
            }
            style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <FilterViewManager
              currentFilters={sharedFiltersState}
              hasInitialFilter={!!initialFilter || !!cardFilterConfig}
              initialViewId={initialViewId}
              selectedViewId={selectedViewId}
              onSelectedViewIdChange={setSelectedViewId}
              viewMode="list"
              onClearViews={(clearFn) => {
                // Store the clear function in a ref so it can be called from onClearFilters
                clearViewsRef.current = clearFn;
              }}
              onLoadView={(
                filters,
                isClearing,
                viewId?: string,
                cachedData?: any[]
              ) => {
                if (!viewId && filters !== null) {
                  return;
                }
                const cached = viewId
                  ? cachedViewDataRef.current.get(viewId)
                  : undefined;
                const dataToUse = cached || cachedData;
                if (isClearing) {
                  if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                  }
                  setSharedFiltersState({});
                  setStatusFilterFromView(null);
                  setUpdateStatusFilter(false);
                  if (mappedReturns) {
                    setDataSource(mappedReturns);
                  }
                } else {
                  if (filters === null) {
                    if (!isLoadingViewRef.current) {
                      isLoadingViewRef.current = true;
                      setSharedFiltersState({});
                      setStatusFilterFromView(null);
                      setUpdateStatusFilter(false);
                      if (mappedReturns) {
                        setDataSource(mappedReturns);
                      }
                      if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                      }
                      loadingTimeoutRef.current = setTimeout(() => {
                        isLoadingViewRef.current = false;
                        loadingTimeoutRef.current = null;
                      }, 150);
                    } else {
                      setSharedFiltersState({});
                      setStatusFilterFromView(null);
                      setUpdateStatusFilter(false);
                      if (mappedReturns) {
                        setDataSource(mappedReturns);
                      }
                    }
                  } else {
                    // One-step apply: view = filter object, set state and data in one go
                    setSharedFiltersState(filters);
                    const statusValues =
                      filters?.status?.value != null
                        ? Array.isArray(filters.status.value)
                          ? filters.status.value
                          : [filters.status.value]
                        : null;
                    if (statusValues?.length) {
                      setStatusFilterFromView(statusValues);
                      setInitialStatusFilter(statusValues);
                      setUpdateStatusFilter(true);
                    } else {
                      setStatusFilterFromView(null);
                      setInitialStatusFilter([]);
                    }
                    const filteredData = applyFiltersPipeline(
                      mappedReturns ?? [],
                      filters,
                      {
                        mainLocaleKey: undefined,
                        excludeSubmittedCancelledForReturnType: true,
                      }
                    );
                    setDataSource(
                      dataToUse && dataToUse.length >= 0
                        ? dataToUse
                        : filteredData ?? mappedReturns ?? []
                    );
                    if (viewId && filters) {
                      const dataForPreload =
                        dataToUse && dataToUse.length >= 0
                          ? dataToUse
                          : filteredData ?? [];
                      preloadViewForAllModes(viewId, filters, dataForPreload);
                    }
                    lastAppliedFiltersRef.current.list = {
                      filters,
                      viewId: viewId ?? undefined,
                    };
                  }
                }
              }}
              handlePreferences={handlePreferences}
              currentUser={currentUser}
            />
            {!listViewOnly && renderExportReturnsButton()}
          </div>
        )}
        {!loading && listViewOnly && mappedReturns && mappedReturns.length > 0 ? (
          <div className="scope-pwa-submissions-export">{renderExportReturnsButton()}</div>
        ) : null}
        {loading ? (
          viewMode === 'list' ? (
            <div
              style={{
                padding: '24px',
                background: 'var(--color-surface-raised, #fff)',
                borderRadius: '4px',
                border: '1px solid var(--color-border, #dbeae3)',
              }}
            >
              <Skeleton
                active
                paragraph={{ rows: 0 }}
                title={{ width: '200px' }}
              />
              <div style={{ marginTop: '24px' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '16px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <Skeleton.Input
                      key={i}
                      active
                      size="default"
                      style={{ flex: 1, height: '32px' }}
                    />
                  ))}
                </div>
                <Skeleton active paragraph={{ rows: 10 }} />
              </div>
              <div
                style={{
                  marginTop: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Skeleton.Input
                  active
                  size="default"
                  style={{ width: '200px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Skeleton.Button
                    active
                    size="default"
                    style={{ width: '32px' }}
                  />
                  <Skeleton.Button
                    active
                    size="default"
                    style={{ width: '32px' }}
                  />
                  <Skeleton.Button
                    active
                    size="default"
                    style={{ width: '100px' }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Views section skeleton - shown above filters when loading */}
              <div
                style={{
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: '35px', height: '16px' }}
                />
                <Skeleton.Input
                  active
                  size="default"
                  style={{
                    width: '200px',
                    height: '32px',
                    borderRadius: '8px',
                  }}
                />
                <Skeleton.Button
                  active
                  size="small"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    minWidth: '24px',
                    padding: 0,
                  }}
                />
                <Skeleton.Button
                  active
                  size="small"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    minWidth: '24px',
                    padding: 0,
                  }}
                />
              </div>
              {renderSharedFilters()}
              {viewMode === 'board' ? (
                <BoardView
                  mappedReturns={[]}
                  vatGenerate={vatGenerate}
                  loading={true}
                  visibleStatuses={visibleStatuses}
                  statusOrder={statusOrder}
                  isClientLocked={isClientLocked}
                />
              ) : (
                <CalendarView
                  mappedReturns={[]}
                  vatGenerate={vatGenerate}
                  loading={true}
                  visibleStatuses={visibleStatuses}
                  statusOrder={statusOrder}
                  isClientLocked={isClientLocked}
                  handlePreferences={handlePreferences}
                  currentUser={currentUser}
                />
              )}
            </>
          )
        ) : mappedReturns && mappedReturns.length > 0 ? (
          viewMode === 'list' ? (
            <>
              {renderSharedFilters()}
              {listViewOnly ? (
                renderPwaSubmissionsList()
              ) : (
                <ScopeTable
                  className="no-title-padding scope-submissions-list-table"
                  originalDataSource={mappedReturns}
                  tableDataSource={displayReturns}
                  columns={columns}
                  pagination={true}
                  rowsPerPage={pageSizeSaved ? pageSizeSaved : 20}
                  enableColumnsCustomization={false}
                  haveBorder
                  showSizeChanger
                  getNewPageSize={savePageSize}
                  defaultSort={['nextDueDate', 'desc']}
                  rowHeight={50}
                />
              )}
            </>
          ) : null
        ) : (
          <EmptyPage
            titleText={
              hasEntities
                ? t('organisation.empty-tax-returns-title')
                : t('organisation.empty-entities-title')
            }
            actionText={
              hasEntities
                ? t('submissions.empty-returns-action-proceed')
                : !currentUser?.canManageOrgs
                ? t('submissions.empty-entities-contact-owner')
                : t('organisation.empty-entities-action')
            }
            buttonText={
              hasEntities
                ? t('common.continue')
                : t('organisation.empty-entities-button')
            }
            actionCallback={emptyPageCallback}
            disableButton={!currentUser?.canManageOrgs}
            showIcon={!hasEntities}
          />
        )}
      </>
    );

    const renderBoardContent = () => (
      <>
        {!loading && (
          <div
            style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <FilterViewManager
              currentFilters={sharedFiltersState}
              hasInitialFilter={!!initialFilter || !!cardFilterConfig}
              initialViewId={initialViewId}
              selectedViewId={selectedViewId}
              onSelectedViewIdChange={setSelectedViewId}
              viewMode="board"
              onClearViews={(clearFn) => {
                // Store the clear function in a ref so it can be called from onClearFilters
                clearViewsRef.current = clearFn;
              }}
              onLoadView={(
                filters,
                isClearing,
                viewId?: string,
                cachedData?: any[]
              ) => {
                if (!viewId && filters !== null) {
                  return;
                }
                const cached = viewId
                  ? cachedViewDataRef.current.get(viewId)
                  : undefined;
                const dataToUse = cached || cachedData;

                // Preload filters for all view modes when a view is selected
                // This will be called after filtering completes in the setTimeout below

                if (isClearing) {
                  if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                  }
                  setSharedFiltersState({});
                  setStatusFilterFromView(null);
                  setUpdateStatusFilter(false);
                  if (mappedReturns) {
                    setDataSource(mappedReturns);
                  }
                } else {
                  if (filters === null) {
                    if (!isLoadingViewRef.current) {
                      isLoadingViewRef.current = true;
                      setSharedFiltersState({});
                      setStatusFilterFromView(null);
                      setUpdateStatusFilter(false);
                      if (mappedReturns) {
                        setDataSource(mappedReturns);
                      }
                      if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                      }
                      loadingTimeoutRef.current = setTimeout(() => {
                        isLoadingViewRef.current = false;
                        loadingTimeoutRef.current = null;
                      }, 150);
                    } else {
                      setSharedFiltersState({});
                      setStatusFilterFromView(null);
                      setUpdateStatusFilter(false);
                      if (mappedReturns) {
                        setDataSource(mappedReturns);
                      }
                    }
                  } else {
                    // One-step apply: view = filter object, set state and data in one go (same as list)
                    setSharedFiltersState(filters);
                    const statusValues =
                      filters?.status?.value != null
                        ? Array.isArray(filters.status.value)
                          ? filters.status.value
                          : [filters.status.value]
                        : null;
                    if (statusValues?.length) {
                      setStatusFilterFromView(statusValues);
                      setInitialStatusFilter(statusValues);
                      setUpdateStatusFilter(true);
                    } else {
                      setStatusFilterFromView(null);
                      setInitialStatusFilter([]);
                    }
                    const filteredData = applyFiltersPipeline(
                      mappedReturns ?? [],
                      filters,
                      {
                        mainLocaleKey: undefined,
                        excludeSubmittedCancelledForReturnType: true,
                      }
                    );
                    setDataSource(
                      dataToUse && dataToUse.length >= 0
                        ? dataToUse
                        : filteredData ?? mappedReturns ?? []
                    );
                    if (viewId && filters) {
                      const dataForPreload =
                        dataToUse && dataToUse.length >= 0
                          ? dataToUse
                          : filteredData ?? [];
                      preloadViewForAllModes(viewId, filters, dataForPreload);
                    }
                    lastAppliedFiltersRef.current.board = {
                      filters,
                      viewId: viewId ?? undefined,
                    };
                  }
                }
              }}
              handlePreferences={handlePreferences}
              currentUser={currentUser}
            />
            {renderExportReturnsButton()}
          </div>
        )}
        {loading ? (
          <>
            {/* Views section skeleton - shown above filters when loading */}
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Skeleton.Input
                active
                size="small"
                style={{ width: '35px', height: '16px' }}
              />
              <Skeleton.Input
                active
                size="default"
                style={{
                  width: '200px',
                  height: '32px',
                  borderRadius: '8px',
                }}
              />
              <Skeleton.Button
                active
                size="small"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  minWidth: '24px',
                  padding: 0,
                }}
              />
              <Skeleton.Button
                active
                size="small"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  minWidth: '24px',
                  padding: 0,
                }}
              />
            </div>
            {renderSharedFilters()}
            <BoardView
              mappedReturns={[]}
              vatGenerate={vatGenerate}
              loading={true}
              visibleStatuses={visibleStatuses}
              statusOrder={statusOrder}
              isClientLocked={isClientLocked}
            />
          </>
        ) : mappedReturns && mappedReturns.length > 0 ? (
          <>
            {renderSharedFilters()}
            <BoardView
              mappedReturns={(() => {
                const data = displayBoardCalendarSource || [];
                const seen = new Set<string>();
                const duplicates: string[] = [];
                data.forEach((item) => {
                  const uniqueKey = item.dataLog?.uuid
                    ? `${item.mappingId}-${item.dataLog.uuid}`
                    : `${item.connectionId}-${item.mappingId}`;
                  if (seen.has(uniqueKey)) {
                    duplicates.push(uniqueKey);
                  } else {
                    seen.add(uniqueKey);
                  }
                });
                return data;
              })()}
              vatGenerate={vatGenerate}
              loading={false}
              visibleStatuses={visibleStatuses}
              statusOrder={statusOrder}
              isClientLocked={isClientLocked}
            />
          </>
        ) : (
          <EmptyPage
            titleText={
              hasEntities
                ? t('organisation.empty-tax-returns-title')
                : t('organisation.empty-entities-title')
            }
            actionText={
              hasEntities
                ? t('submissions.empty-returns-action-proceed')
                : !currentUser?.canManageOrgs
                ? t('submissions.empty-entities-contact-owner')
                : t('organisation.empty-entities-action')
            }
            buttonText={
              hasEntities
                ? t('common.continue')
                : t('organisation.empty-entities-button')
            }
            actionCallback={emptyPageCallback}
            disableButton={!currentUser?.canManageOrgs}
            showIcon={!hasEntities}
          />
        )}
      </>
    );

    const renderCalendarContent = () => (
      <>
        {!loading && (
          <div
            style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <FilterViewManager
              currentFilters={sharedFiltersState}
              hasInitialFilter={!!initialFilter || !!cardFilterConfig}
              initialViewId={initialViewId}
              selectedViewId={selectedViewId}
              onSelectedViewIdChange={setSelectedViewId}
              viewMode="calendar"
              onClearViews={(clearFn) => {
                // Store the clear function in a ref so it can be called from onClearFilters
                clearViewsRef.current = clearFn;
              }}
              onLoadView={(
                filters,
                isClearing,
                viewId?: string,
                cachedData?: any[]
              ) => {
                if (!viewId && filters !== null) {
                  return;
                }
                const cached = viewId
                  ? cachedViewDataRef.current.get(viewId)
                  : undefined;
                const dataToUse = cached || cachedData;

                // Preload filters for all view modes when a view is selected
                // This will be called after filtering completes in the setTimeout below

                if (isClearing) {
                  if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                  }
                  setSharedFiltersState({});
                  setStatusFilterFromView(null);
                  setUpdateStatusFilter(false);
                  if (mappedReturns) {
                    setDataSource(mappedReturns);
                  }
                } else {
                  if (filters === null) {
                    if (!isLoadingViewRef.current) {
                      isLoadingViewRef.current = true;
                      setSharedFiltersState({});
                      setStatusFilterFromView(null);
                      setUpdateStatusFilter(false);
                      if (mappedReturns) {
                        setDataSource(mappedReturns);
                      }
                      if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                      }
                      loadingTimeoutRef.current = setTimeout(() => {
                        isLoadingViewRef.current = false;
                        loadingTimeoutRef.current = null;
                      }, 150);
                    } else {
                      setSharedFiltersState({});
                      setStatusFilterFromView(null);
                      setUpdateStatusFilter(false);
                      if (mappedReturns) {
                        setDataSource(mappedReturns);
                      }
                    }
                  } else {
                    // One-step apply: view = filter object, set state and data in one go (same as list)
                    setSharedFiltersState(filters);
                    const statusValues =
                      filters?.status?.value != null
                        ? Array.isArray(filters.status.value)
                          ? filters.status.value
                          : [filters.status.value]
                        : null;
                    if (statusValues?.length) {
                      setStatusFilterFromView(statusValues);
                      setInitialStatusFilter(statusValues);
                      setUpdateStatusFilter(true);
                    } else {
                      setStatusFilterFromView(null);
                      setInitialStatusFilter([]);
                    }
                    const filteredData = applyFiltersPipeline(
                      mappedReturns ?? [],
                      filters,
                      {
                        mainLocaleKey: undefined,
                        excludeSubmittedCancelledForReturnType: true,
                      }
                    );
                    setDataSource(
                      dataToUse && dataToUse.length >= 0
                        ? dataToUse
                        : filteredData ?? mappedReturns ?? []
                    );
                    if (viewId && filters) {
                      const dataForPreload =
                        dataToUse && dataToUse.length >= 0
                          ? dataToUse
                          : filteredData ?? [];
                      preloadViewForAllModes(viewId, filters, dataForPreload);
                    }
                    lastAppliedFiltersRef.current.calendar = {
                      filters,
                      viewId: viewId ?? undefined,
                    };
                  }
                }
              }}
              handlePreferences={handlePreferences}
              currentUser={currentUser}
            />
            {renderExportReturnsButton()}
          </div>
        )}
        {loading ? (
          <>
            {/* Views section skeleton - shown above filters when loading */}
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Skeleton.Input
                active
                size="small"
                style={{ width: '35px', height: '16px' }}
              />
              <Skeleton.Input
                active
                size="default"
                style={{
                  width: '200px',
                  height: '32px',
                  borderRadius: '8px',
                }}
              />
              <Skeleton.Button
                active
                size="small"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  minWidth: '24px',
                  padding: 0,
                }}
              />
              <Skeleton.Button
                active
                size="small"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  minWidth: '24px',
                  padding: 0,
                }}
              />
            </div>
            {renderSharedFilters()}
            <CalendarView
              mappedReturns={[]}
              vatGenerate={vatGenerate}
              loading={true}
              visibleStatuses={visibleStatuses}
              statusOrder={statusOrder}
              isClientLocked={isClientLocked}
              handlePreferences={handlePreferences}
              currentUser={currentUser}
            />
          </>
        ) : mappedReturns && mappedReturns.length > 0 ? (
          <>
            {renderSharedFilters()}
            <CalendarView
              mappedReturns={(() => {
                const data = displayBoardCalendarSource || [];
                const seen = new Set<string>();
                data.forEach((item) => {
                  const uniqueKey = item.dataLog?.uuid
                    ? `${item.mappingId}-${item.dataLog.uuid}`
                    : `${item.connectionId}-${item.mappingId}`;
                  seen.add(uniqueKey);
                });
                return data;
              })()}
              vatGenerate={vatGenerate}
              loading={false}
              visibleStatuses={visibleStatuses}
              statusOrder={statusOrder}
              isClientLocked={isClientLocked}
              handlePreferences={handlePreferences}
              currentUser={currentUser}
            />
          </>
        ) : (
          <EmptyPage
            titleText={
              hasEntities
                ? t('organisation.empty-tax-returns-title')
                : t('organisation.empty-entities-title')
            }
            actionText={
              hasEntities
                ? t('submissions.empty-returns-action-proceed')
                : !currentUser?.canManageOrgs
                ? t('submissions.empty-entities-contact-owner')
                : t('organisation.empty-entities-action')
            }
            buttonText={
              hasEntities
                ? t('common.continue')
                : t('organisation.empty-entities-button')
            }
            actionCallback={emptyPageCallback}
            disableButton={!currentUser?.canManageOrgs}
            showIcon={!hasEntities}
          />
        )}
      </>
    );

    return [
      {
        key: 'list',
        label: (
          <Space>
            <UnorderedListOutlined />
            {t('dashboard.view-list')}
          </Space>
        ),
        children: renderListContent(),
      },
      {
        key: 'board',
        label: (
          <Space>
            <AppstoreOutlined />
            {t('submissions.view-board')}
          </Space>
        ),
        children: renderBoardContent(),
      },
      {
        key: 'calendar',
        label: (
          <Space>
            <CalendarOutlined />
            {t('dashboard.card-title-calendar')}
          </Space>
        ),
        children: renderCalendarContent(),
      },
    ];
  }, [
    viewMode,
    loading,
    mappedReturns,
    filteredReturns,
    visibleFilters,
    selectedContanier,
    initialFilter,
    initialViewId,
    selectedViewId,
    sharedFiltersState,
    statusFilterFromView,
    initialStatusFilter,
    visibleStatuses,
    statusOrder,
    isClientLocked,
    hasEntities,
    currentUser,
    columns,
    pageSizeSaved,
    updateDueDateFilter,
    updateStatusFilter,
    selectedPresetFilters,
    renderSharedFilters,
    renderPwaSubmissionsList,
    listViewOnly,
    t,
    calcDueReturns,
    calcExpiredReturns,
    calcAwaitingClientReturns,
    filterDueReturns,
  ]);

  const submissionsTableContextValue = useMemo(
    () => ({
      currentUser,
      hasAdvancedUserRights,
      gettingTaxes: !!gettingTaxes,
      isClientLocked,
    }),
    [currentUser, hasAdvancedUserRights, gettingTaxes, isClientLocked]
  );

  const submissionsMenuContextValue = useMemo(
    () => ({ openMenuKey }),
    [openMenuKey]
  );

  return (
    <SubmissionsTableContext.Provider value={submissionsTableContextValue}>
    <SubmissionsMenuContext.Provider value={submissionsMenuContextValue}>
    <div
      className={listViewOnly ? 'scope-pwa-submissions-list' : undefined}
      style={{
        ...(listViewOnly
          ? {}
          : {
              padding: '40px',
              paddingTop: '10px',
              paddingBottom: viewMode === 'board' ? 0 : '40px',
            }),
        maxWidth: listViewOnly ? '100%' : hasScrollbar ? 1662 : 1678,
        minWidth: listViewOnly ? 0 : 1200,
        margin: 'auto',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {showCard && !listViewOnly && !pwaMobileNav && (
        <MainGuide
          setShowCard={setShowCard}
          handlePreferences={handlePreferences}
        />
      )}
      {listViewOnly && (
        <Typography.Title level={4} className="scope-pwa-page-title">
          {t('main.tax-returns')}
        </Typography.Title>
      )}
      <Row
        style={{
          marginTop: showCard && !listViewOnly && !pwaMobileNav ? 20 : -5,
          marginBottom: 20,
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      >
        <ViewTabsContainer>
          {listViewOnly ? (
            tabItems.find((item) => item.key === 'list')?.children
          ) : (
            <ScopeTabs
              activeKey={viewMode}
              onChange={(key) => {
                const newViewMode = key as 'list' | 'board' | 'calendar';
                setViewMode(newViewMode);
                handlePreferences(PreferenceActions.SET, {
                  name: MAIN_VIEW_MODE,
                  value: newViewMode,
                });
              }}
              items={tabItems}
            />
          )}
        </ViewTabsContainer>
        <div
          style={{
            position: 'absolute',
            insetInlineEnd: 0,
            top: 0,
            display: listViewOnly || pwaMobileNav ? 'none' : 'flex',
            gap: '8px',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          {viewMode === 'board' && (
            <ScopeButton
              type="default"
              width="100px"
              height="30px"
              fontSize="12px"
              lineheight="12px"
              onClick={openStatusSettings}
              style={{
                backgroundColor: 'var(--color-surface, #FFFFFF)',
                borderColor: 'var(--color-border, #d9d9d9)',
                color: 'var(--color-text-base, var(--color-text-dark, #042a2a))',
                padding: '4px 5px',
              }}
            >
              {t('submissions.edit-board')}
            </ScopeButton>
          )}
          <ExportButton
            type="text"
            onClick={() => {
              setShowCard(!showCard);
              handlePreferences(PreferenceActions.SET, {
                name: MAIN_TUTORIAL_HIDE,
                value: !showCard,
              });
            }}
            icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
            isGuide
          >
            {showCard ? t('submissions.close-guide') : t('organisation.open-guide')}
          </ExportButton>
        </div>
      </Row>
    </div>
    </SubmissionsMenuContext.Provider>
    </SubmissionsTableContext.Provider>
  );
};
